import React, { useEffect, useMemo, useState } from 'react';
import AuthorizationHoldService from './AuthorizationHoldService';
import AuthorizationHoldFlagService from './AuthorizationHoldFlagService';
import patientFileService from '../PatientFile/PatientFileService.js';
import PatientAuthorizationService from '../Authorization/authorizationService';
import clinicalNoteService from '../clinicalNotes/clinicalNoteService.js';
import authorizationClinicalProfileService from '../clinicalNotes/authorizationClinicalProfileService.js';
import apiBaseUrl from '../../utils/config.js';
import { authFetch } from '../../utils/authFetch.js';
import '../registration/searchPopupDesign.css';
import './AuthorizationHoldPage.css';

const RESULT_STATUSES = ['APPROVED', 'DENIED', 'CANCELLED', 'CLOSED'];

const WORKFLOW_TABS = [
  { key: 'create', title: 'Create Hold' },
  { key: 'waitingForNotes', title: 'Waiting for Notes' },
  { key: 'notesComplete', title: 'Notes Complete' },
  { key: 'authorization', title: 'Authorization' },
  { key: 'onHold', title: 'On Hold' },
  { key: 'results', title: 'Results' },
];

const EMPTY_CLINICAL_PROFILE_MODAL = {
  open: false,
  authId: '',
  superbillId: '',
  patientName: '',
  bodyPartTemplateId: '',
  laterality: '',
  providerId: '',
  providerName: '',
  saving: false,
  error: '',
};

const AuthorizationHoldPage = () => {
  const [board, setBoard] = useState({
    waitingForNotes: [],
    notesComplete: [],
    sentToAuthorization: [],
    waitingForAuthorization: [],
    onHold: [],
    results: [],
    doctors: [],
  });

  const [activeTab, setActiveTab] = useState('waitingForNotes');
  const [doctorFilter, setDoctorFilter] = useState('');
  const [patientSearchTerm, setPatientSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [resultsPage, setResultsPage] = useState(0);
  const [resultsData, setResultsData] = useState({
    content: [], totalElements: 0, totalPages: 0, number: 0,
  });
  const [resultsLoading, setResultsLoading] = useState(false);
  const [resultsError, setResultsError] = useState('');
  const [resultsRefreshKey, setResultsRefreshKey] = useState(0);

  const [expandedHoldSections, setExpandedHoldSections] = useState({});
  const [expandedActionSections, setExpandedActionSections] = useState({});
  const [expandedSuperbills, setExpandedSuperbills] = useState({});
  const [visibleSuperbillLimitByHold, setVisibleSuperbillLimitByHold] =
    useState({});

  const [activeFlagDefinitions, setActiveFlagDefinitions] = useState([]);
  const [flagSelectionByHold, setFlagSelectionByHold] = useState({});
  const [flagNotesByHold, setFlagNotesByHold] = useState({});

  const [patientFilesByHold, setPatientFilesByHold] = useState({});
  const [patientFileUsageByHold, setPatientFileUsageByHold] = useState({});
  const [patientFilesLoadingByHold, setPatientFilesLoadingByHold] = useState({});
  const [patientFileUploadByHold, setPatientFileUploadByHold] = useState({});
  const [patientFileCustomNameByHold, setPatientFileCustomNameByHold] = useState({});

  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [clinicalNoteLoadingBySuperbill, setClinicalNoteLoadingBySuperbill] = useState({});
  const [activeBodyParts, setActiveBodyParts] = useState([]);
  const [clinicalProfileModal, setClinicalProfileModal] = useState({
    ...EMPTY_CLINICAL_PROFILE_MODAL,
  });

  const [patientId, setPatientId] = useState('');
  const [patientName, setPatientName] = useState('');
  const [authorizations, setAuthorizations] = useState([]);
  const [selectedAuthId, setSelectedAuthId] = useState('');
  const [holdReason, setHoldReason] = useState('');
  const [assignedDoctorId, setAssignedDoctorId] = useState('');
  const [assignedDoctorName, setAssignedDoctorName] = useState('');
  const [selectedCreateFlagIds, setSelectedCreateFlagIds] = useState([]);
  const [requestedSuperbillDates, setRequestedSuperbillDates] = useState('');
  const [pendingAuthCode, setPendingAuthCode] = useState('');

  const [doctorNotesByHold, setDoctorNotesByHold] = useState({});
  const [authorizationNotesByHold, setAuthorizationNotesByHold] = useState({});
  const [pendingAuthCodeByHold, setPendingAuthCodeByHold] = useState({});
  const [resultStatusByHold, setResultStatusByHold] = useState({});
  const [resultNotesByHold, setResultNotesByHold] = useState({});
  const [noAuthNotesByHold, setNoAuthNotesByHold] = useState({});
  const [diagnosisByHold, setDiagnosisByHold] = useState({});
  const [approvedAuthFormByHold, setApprovedAuthFormByHold] = useState({});
  const [clinicalEpisodesByPatient, setClinicalEpisodesByPatient] = useState({});
  const [clinicalEpisodeLoadingByPatient, setClinicalEpisodeLoadingByPatient] = useState({});
  const [clinicalEpisodeErrorByPatient, setClinicalEpisodeErrorByPatient] = useState({});
  const [approvedAuthSavingByHold, setApprovedAuthSavingByHold] = useState({});
  const [workflowNotesByHold, setWorkflowNotesByHold] = useState({});
  const [focusedHold, setFocusedHold] = useState(null);
  const [focusedPanel, setFocusedPanel] = useState('overview');
  const [focusedSectionKey, setFocusedSectionKey] = useState('');

  const service = new AuthorizationHoldService();
  const flagService = new AuthorizationHoldFlagService();
  const authService = new PatientAuthorizationService();

  const role = localStorage.getItem('role')?.replace('ROLE_', '') || '';

  const currentDoctorId =
    localStorage.getItem('doctorId') ||
    sessionStorage.getItem('doctorId') ||
    '';

  const canManageAuth =
    role === 'ADMIN' || role === 'MANAGER' || role === 'FRONTDESK';

  const canDoctorNotes =
    role === 'ADMIN' ||
    role === 'MANAGER' ||
    role === 'FRONTDESK' ||
    role === 'DOCTOR';

  const canDelete = role === 'ADMIN' || role === 'MANAGER';

  const canAccessClinicalNotes = ['ADMIN', 'MANAGER', 'DOCTOR'].includes(role);
  const canConfigureClinicalProfile = ['ADMIN', 'MANAGER', 'DOCTOR'].includes(role);

  useEffect(() => {
    const handleMessage = async (event) => {
      const { inputTarget, value, displayName, selectedItem } = event.data || {};

      if (inputTarget === 'authorizationHoldPatientId') {
        setPatientId(value);
        setPatientName(displayName || `Patient #${value}`);
        setSelectedAuthId('');
        setAuthorizations([]);

        if (value) {
          await loadPatientAuthorizations(value);
        }
      }

      if (inputTarget === 'authorizationHoldDoctorId') {
        setAssignedDoctorId(value);
        setAssignedDoctorName(displayName || `Doctor #${value}`);
      }

      if (inputTarget === 'authorizationClinicalProfileProvider') {
        setClinicalProfileModal((prev) => ({
          ...prev,
          providerId: value || '',
          providerName: displayName || (value ? `Doctor #${value}` : ''),
          error: '',
        }));
      }

      if (String(inputTarget || '').startsWith('approvedAuthProvider_')) {
        const holdId = String(inputTarget).replace('approvedAuthProvider_', '');

        updateApprovedAuthForm(holdId, 'providerId', value || '');
        updateApprovedAuthForm(
          holdId,
          'providerName',
          displayName || (value ? `Doctor #${value}` : '')
        );
      }

      if (String(inputTarget || '').startsWith('authorizationHoldDiagnosis_')) {
        const holdId = String(inputTarget).replace(
          'authorizationHoldDiagnosis_',
          ''
        );

        const diagnosisCode = selectedItem?.code || displayName || value || '';

        if (diagnosisCode) {
          addDiagnosisToHold(holdId, diagnosisCode);
        }
      }

      if (String(inputTarget || '').startsWith('approvedAuthDiagnosis_')) {
        const holdId = String(inputTarget).replace(
          'approvedAuthDiagnosis_',
          ''
        );

        const diagnosisCode = selectedItem?.code || displayName || value || '';

        if (diagnosisCode) {
          addApprovedAuthCode(holdId, 'diagnosisSet', diagnosisCode);
        }
      }

      if (String(inputTarget || '').startsWith('approvedAuthProcedure_')) {
        const holdId = String(inputTarget).replace(
          'approvedAuthProcedure_',
          ''
        );

        const procedureCode = selectedItem?.code || displayName || value || '';

        if (procedureCode) {
          addApprovedAuthCode(holdId, 'procedureSet', procedureCode);
        }
      }
    };

    window.addEventListener('message', handleMessage);

    return () => window.removeEventListener('message', handleMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadBoard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctorFilter]);

  // Search the active board immediately, but debounce historical database searches.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(patientSearchTerm.trim()), 350);
    return () => clearTimeout(timer);
  }, [patientSearchTerm]);

  // Historical records are fetched ONLY on Results or during an explicit search.
  useEffect(() => {
    if (activeTab !== 'results' && !debouncedSearchTerm) return;

    let cancelled = false;
    const loadResults = async () => {
      setResultsLoading(true);
      setResultsError('');
      try {
        const params = new URLSearchParams({
          page: String(resultsPage), size: '20', search: debouncedSearchTerm,
        });
        if (doctorFilter) params.set('doctorId', doctorFilter);

        const token = localStorage.getItem('jwt');
        const response = await fetch(
          `${apiBaseUrl}/authorization-holds/results?${params.toString()}`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        );
        const text = await response.text();
        let data = null;
        try { data = text ? JSON.parse(text) : null; } catch { data = null; }
        if (!response.ok) {
          throw new Error(data?.message || data?.error || `Results request failed (${response.status}).`);
        }
        if (!cancelled) {
          setResultsData({
            content: Array.isArray(data?.content) ? data.content : [],
            totalElements: Number(data?.totalElements || 0),
            totalPages: Number(data?.totalPages || 0),
            number: Number(data?.number || 0),
          });
        }
      } catch (err) {
        if (!cancelled) {
          setResultsError(err.message || 'Could not load historical results.');
          setResultsData({ content: [], totalElements: 0, totalPages: 0, number: 0 });
        }
      } finally {
        if (!cancelled) setResultsLoading(false);
      }
    };
    loadResults();
    return () => { cancelled = true; };
  }, [activeTab, debouncedSearchTerm, doctorFilter, resultsPage, resultsRefreshKey]);

  useEffect(() => { setResultsPage(0); }, [doctorFilter, debouncedSearchTerm]);

  useEffect(() => {
    loadActiveFlagDefinitions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadActiveBodyParts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showSuccess = (message) => {
    setSuccess(message);
    setTimeout(() => setSuccess(''), 3500);
  };

  const loadBoard = async () => {
    setResultsRefreshKey((value) => value + 1);
    try {
      setLoading(true);
      setError('');

      const data = await service.getBoard(doctorFilter);

      setBoard({
        waitingForNotes: Array.isArray(data?.waitingForNotes)
          ? data.waitingForNotes
          : [],
        notesComplete: Array.isArray(data?.notesComplete)
          ? data.notesComplete
          : [],
        sentToAuthorization: Array.isArray(data?.sentToAuthorization)
          ? data.sentToAuthorization
          : [],
        waitingForAuthorization: Array.isArray(data?.waitingForAuthorization)
          ? data.waitingForAuthorization
          : [],
        onHold: Array.isArray(data?.onHold) ? data.onHold : [],
        results: [],
        doctors: Array.isArray(data?.doctors) ? data.doctors : [],
      });
    } catch (err) {
      console.error('Failed to load authorization hold board:', err);
      setError(err.message || 'Failed to load authorization hold board.');
    } finally {
      setLoading(false);
    }
  };

  const loadPatientAuthorizations = async (selectedPatientId) => {
    try {
      const auths = await authService.findByPatientId(selectedPatientId);
      setAuthorizations(Array.isArray(auths) ? auths : []);
    } catch (err) {
      console.error('Failed to load patient authorizations:', err);
      setAuthorizations([]);
      alert('Could not load authorizations for this patient.');
    }
  };

  const loadActiveFlagDefinitions = async () => {
    try {
      const flags = await flagService.getActiveDefinitions();
      setActiveFlagDefinitions(Array.isArray(flags) ? flags : []);
    } catch (err) {
      console.error('Failed to load authorization hold flags:', err);
      setActiveFlagDefinitions([]);
    }
  };

  const loadActiveBodyParts = async () => {
    try {
      const bodyParts = await authorizationClinicalProfileService.getActiveBodyParts();
      setActiveBodyParts(Array.isArray(bodyParts) ? bodyParts : []);
    } catch (err) {
      console.error('Failed to load active clinical body parts:', err);
      setActiveBodyParts([]);
    }
  };

  const getStaffId = () => {
    return (
      localStorage.getItem('doctorId') ||
      sessionStorage.getItem('doctorId') ||
      currentDoctorId ||
      ''
    );
  };

  const closeExpandedSectionsForHold = (holdId) => {
    if (!holdId) {
      return;
    }

    setExpandedHoldSections((prev) => ({
      ...prev,
      [holdId]: {},
    }));
  };

  const updateFocusedHoldAfterRefresh = async (holdId) => {
    if (!holdId) {
      return;
    }

    try {
      const data = await service.getBoard(doctorFilter);
      const allHolds = [
        ...(Array.isArray(data?.waitingForNotes) ? data.waitingForNotes : []),
        ...(Array.isArray(data?.notesComplete) ? data.notesComplete : []),
        ...(Array.isArray(data?.sentToAuthorization) ? data.sentToAuthorization : []),
        ...(Array.isArray(data?.waitingForAuthorization)
          ? data.waitingForAuthorization
          : []),
        ...(Array.isArray(data?.onHold) ? data.onHold : []),
        ...(Array.isArray(data?.results) ? data.results : []),
      ];

      const freshHold = allHolds.find(
        (item) => String(item.holdId) === String(holdId)
      );

      if (freshHold) {
        setFocusedHold(freshHold);
        setFocusedSectionKey(getSectionKeyForHold(freshHold));
      } else if (focusedHold && String(focusedHold.holdId) === String(holdId)) {
        // A hold may have moved to Results, which is no longer in /board.
        setFocusedHold(null);
        setFocusedPanel('overview');
        setFocusedSectionKey('');
      }
    } catch {
      // Keep the popup open with the last visible data if refresh fails.
    }
  };

  const openHoldPopup = (hold, panel = 'overview', sectionKey = '') => {
    setFocusedHold(hold);
    setFocusedPanel(panel);
    setFocusedSectionKey(sectionKey || getSectionKeyForHold(hold));
  };

  const closeHoldPopup = () => {
    setFocusedHold(null);
    setFocusedPanel('overview');
    setFocusedSectionKey('');
  };

  const openPatientSearch = () => {
    const popupUrl = `/search-popup?type=Patient&inputTarget=authorizationHoldPatientId&url=${encodeURIComponent(
      apiBaseUrl + '/patientinfo/search/all'
    )}`;

    window.open(popupUrl, 'popup', 'width=700,height=700');
  };

  const openDoctorSearch = () => {
    const popupUrl = `/search-popup?type=Doctor&inputTarget=authorizationHoldDoctorId&url=${encodeURIComponent(
      apiBaseUrl + '/doctorinfo/search/all'
    )}`;

    window.open(popupUrl, 'popup', 'width=700,height=700');
  };

  const openClinicalProviderSearch = (inputTarget) => {
    const popupUrl = `/search-popup?type=Doctor&inputTarget=${encodeURIComponent(
      inputTarget
    )}&url=${encodeURIComponent(apiBaseUrl + '/doctorinfo/search/all')}`;

    window.open(popupUrl, 'popup', 'width=700,height=700');
  };

  const openDiagnosisSearch = (holdId) => {
    const popupUrl = `/select-procedure-diagnosis?type=diagnosis&inputTarget=authorizationHoldDiagnosis_${holdId}`;
    window.open(popupUrl, 'popup', 'width=900,height=700');
  };

  const openApprovedAuthDiagnosisSearch = (holdId) => {
    const popupUrl = `/select-procedure-diagnosis?type=diagnosis&inputTarget=approvedAuthDiagnosis_${holdId}`;
    window.open(popupUrl, 'popup', 'width=900,height=700');
  };

  const openApprovedAuthProcedureSearch = (holdId) => {
    const popupUrl = `/select-procedure-diagnosis?type=procedure&inputTarget=approvedAuthProcedure_${holdId}`;
    window.open(popupUrl, 'popup', 'width=900,height=700');
  };

  const parseCommaCodes = (value) => {
    if (!value || !String(value).trim()) {
      return [];
    }

    return String(value)
      .split(',')
      .map((item) => item.trim())
      .filter((item) => item !== '');
  };

  const joinUniqueCodes = (existingValue, newCode) => {
    const cleanCode = String(newCode || '').trim();

    if (!cleanCode) {
      return existingValue || '';
    }

    const pieces = parseCommaCodes(existingValue);

    if (!pieces.includes(cleanCode)) {
      pieces.push(cleanCode);
    }

    return pieces.join(',');
  };

  const getDiagnosisTextForHold = (hold) => {
    return diagnosisByHold[hold.holdId] !== undefined
      ? diagnosisByHold[hold.holdId]
      : hold.updatedDiagnosisSet || hold.diagnosisSet || '';
  };

  const addDiagnosisToHold = (holdId, diagnosisCode) => {
    setDiagnosisByHold((prev) => ({
      ...prev,
      [holdId]: joinUniqueCodes(prev[holdId] || '', diagnosisCode),
    }));
  };

  const removeDiagnosisFromHold = (hold, codeToRemove) => {
    setDiagnosisByHold((prev) => {
      const currentText =
        prev[hold.holdId] !== undefined
          ? prev[hold.holdId]
          : hold.updatedDiagnosisSet || hold.diagnosisSet || '';

      const updatedPieces = parseCommaCodes(currentText).filter(
        (code) => code !== codeToRemove
      );

      return {
        ...prev,
        [hold.holdId]: updatedPieces.join(','),
      };
    });
  };

  const clearDiagnosisForHold = (hold) => {
    const confirmed = window.confirm(
      'Clear all selected diagnoses for this hold?'
    );

    if (!confirmed) {
      return;
    }

    setDiagnosisByHold((prev) => ({
      ...prev,
      [hold.holdId]: '',
    }));
  };

  const getApprovedAuthDefaults = (hold) => {
    const today = new Date().toISOString().split('T')[0];

    return {
      authCode: hold.pendingAuthCode || hold.approvedAuthCode || '',
      startDate: today,
      endDate: '',
      totalVisits: '',
      active: 'yes',
      referredBy: '',
      resultNotes: '',
      diagnosisSet: '',
      procedureSet: '',
      bodyPartTemplateId: '',
      laterality: '',
      providerId: '',
      providerName: '',
      clinicalEpisodeId: '',
    };
  };

  const getApprovedAuthForm = (hold) => {
    return {
      ...getApprovedAuthDefaults(hold),
      ...(approvedAuthFormByHold[hold.holdId] || {}),
    };
  };

  const updateApprovedAuthForm = (holdId, key, value) => {
    setApprovedAuthFormByHold((prev) => ({
      ...prev,
      [holdId]: {
        ...(prev[holdId] || {}),
        [key]: value,
      },
    }));
  };

  const addApprovedAuthCode = (holdId, key, code) => {
    setApprovedAuthFormByHold((prev) => {
      const current = prev[holdId] || {};
      const updatedValue = joinUniqueCodes(current[key] || '', code);

      return {
        ...prev,
        [holdId]: {
          ...current,
          [key]: updatedValue,
        },
      };
    });
  };

  const removeApprovedAuthCode = (holdId, key, codeToRemove) => {
    setApprovedAuthFormByHold((prev) => {
      const current = prev[holdId] || {};
      const updatedPieces = parseCommaCodes(current[key] || '').filter(
        (code) => code !== codeToRemove
      );

      return {
        ...prev,
        [holdId]: {
          ...current,
          [key]: updatedPieces.join(','),
        },
      };
    });
  };

  const clearApprovedAuthCodes = (holdId, key, label) => {
    const confirmed = window.confirm(`Clear all selected ${label}?`);

    if (!confirmed) {
      return;
    }

    updateApprovedAuthForm(holdId, key, '');
  };

  const copyDiagnosisFromHold = (hold) => {
    const diagnosis =
      hold.updatedDiagnosisSet ||
      hold.diagnosisSet ||
      getDiagnosisTextForHold(hold) ||
      '';

    if (!diagnosis.trim()) {
      alert('No diagnosis found on this hold to copy.');
      return;
    }

    updateApprovedAuthForm(hold.holdId, 'diagnosisSet', diagnosis);
  };

  const copyProcedureFromHold = (hold) => {
    const procedure = hold.procedureSet || '';

    if (!procedure.trim()) {
      alert('No procedure found on this hold to copy.');
      return;
    }

    updateApprovedAuthForm(hold.holdId, 'procedureSet', procedure);
  };

  const getBodyPartById = (bodyPartTemplateId) => {
    const id = Number(bodyPartTemplateId);

    return activeBodyParts.find(
      (bodyPart) => Number(bodyPart.bodyPartTemplateId) === id
    );
  };

  const getLateralityOptions = (bodyPartTemplateId) => {
    const bodyPart = getBodyPartById(bodyPartTemplateId);

    if (!bodyPart) {
      return [];
    }

    if (!bodyPart.supportsLaterality) {
      return [{ value: 'NOT_APPLICABLE', label: 'N/A' }];
    }

    const options = [
      { value: 'LEFT', label: 'Left' },
      { value: 'RIGHT', label: 'Right' },
    ];

    if (bodyPart.allowsBilateral) {
      options.push({ value: 'BILATERAL', label: 'Bilateral' });
    }

    return options;
  };

  const getDefaultLaterality = (bodyPartTemplateId) => {
    const bodyPart = getBodyPartById(bodyPartTemplateId);

    if (!bodyPart) {
      return '';
    }

    return bodyPart.supportsLaterality ? '' : 'NOT_APPLICABLE';
  };

  const handleApprovedAuthBodyPartChange = (holdId, bodyPartTemplateId) => {
    updateApprovedAuthForm(holdId, 'bodyPartTemplateId', bodyPartTemplateId);
    updateApprovedAuthForm(
      holdId,
      'laterality',
      getDefaultLaterality(bodyPartTemplateId)
    );
  };

  const isClinicalProfileConfigurationError = (err) => {
    const message = String(err?.message || '').toLowerCase();

    return (
      message.includes('clinical profile') ||
      message.includes('body part must be configured') ||
      message.includes('laterality must be configured') ||
      message.includes('treating/signing provider must be configured') ||
      message.includes('authorization clinical profile is inactive')
    );
  };

  const extractAuthorizationIdFromError = (err) => {
    const message = String(err?.message || '');
    const match = message.match(/Authorization\s+(\d+)/i);

    return match ? Number(match[1]) : null;
  };

  const closeClinicalProfileModal = () => {
    if (clinicalProfileModal.saving) {
      return;
    }

    setClinicalProfileModal({ ...EMPTY_CLINICAL_PROFILE_MODAL });
  };

  const openClinicalProfileSetup = async ({
    authId,
    superbillId,
    patientName: modalPatientName,
  }) => {
    const cleanAuthId = Number(authId);
    const cleanSuperbillId = Number(superbillId);

    if (!Number.isInteger(cleanAuthId) || cleanAuthId <= 0) {
      alert(
        'The clinical note needs authorization setup, but the authorization ID could not be determined.'
      );
      return;
    }

    let existingProfile = null;

    try {
      existingProfile = await authorizationClinicalProfileService.getByAuthorization(
        cleanAuthId
      );
    } catch (profileError) {
      const message = String(profileError?.message || '').toLowerCase();

      if (!message.includes('does not have a clinical profile')) {
        console.warn('Could not pre-load authorization clinical profile:', profileError);
      }
    }

    const bodyPartTemplateId = existingProfile?.bodyPartTemplateId || '';

    setClinicalProfileModal({
      open: true,
      authId: cleanAuthId,
      superbillId: cleanSuperbillId,
      patientName: modalPatientName || '',
      bodyPartTemplateId,
      laterality:
        existingProfile?.laterality || getDefaultLaterality(bodyPartTemplateId),
      providerId: existingProfile?.providerId || '',
      providerName: existingProfile?.providerName || '',
      saving: false,
      error: '',
    });
  };

  const updateClinicalProfileModal = (key, value) => {
    setClinicalProfileModal((prev) => ({
      ...prev,
      [key]: value,
      error: '',
    }));
  };

  const handleClinicalProfileBodyPartChange = (bodyPartTemplateId) => {
    setClinicalProfileModal((prev) => ({
      ...prev,
      bodyPartTemplateId,
      laterality: getDefaultLaterality(bodyPartTemplateId),
      error: '',
    }));
  };

  const saveClinicalProfileAndOpenNote = async () => {
    if (!canConfigureClinicalProfile) {
      updateClinicalProfileModal(
        'error',
        'You do not have permission to configure the authorization clinical profile.'
      );
      return;
    }

    const authId = Number(clinicalProfileModal.authId);
    const superbillId = Number(clinicalProfileModal.superbillId);
    const bodyPartTemplateId = Number(clinicalProfileModal.bodyPartTemplateId);
    const providerId = Number(clinicalProfileModal.providerId);
    const staffId = Number(getStaffId());

    if (!Number.isInteger(bodyPartTemplateId) || bodyPartTemplateId <= 0) {
      updateClinicalProfileModal('error', 'Please select a body part.');
      return;
    }

    if (!clinicalProfileModal.laterality) {
      updateClinicalProfileModal('error', 'Please select laterality.');
      return;
    }

    if (!Number.isInteger(providerId) || providerId <= 0) {
      updateClinicalProfileModal(
        'error',
        'Please select the treating/signing provider.'
      );
      return;
    }

    if (!Number.isInteger(staffId) || staffId <= 0) {
      updateClinicalProfileModal(
        'error',
        'No staff/doctor ID was found. Please log in again.'
      );
      return;
    }

    if (!Number.isInteger(superbillId) || superbillId <= 0) {
      updateClinicalProfileModal('error', 'A valid superbill ID is required.');
      return;
    }

    setClinicalProfileModal((prev) => ({
      ...prev,
      saving: true,
      error: '',
    }));

    try {
      await authorizationClinicalProfileService.saveForAuthorization(authId, {
        bodyPartTemplateId,
        laterality: clinicalProfileModal.laterality,
        providerId,
        active: true,
        staffId,
      });

      let note = null;

      try {
        note = await clinicalNoteService.createClinicalNote({ superbillId });
      } catch (createError) {
        const nowExists = await clinicalNoteService
          .existsForSuperbill(superbillId)
          .catch(() => false);

        if (!nowExists) {
          throw createError;
        }

        note = await clinicalNoteService.getClinicalNoteBySuperbill(superbillId);
      }

      const clinicalNoteId = getClinicalNoteId(note);

      if (!clinicalNoteId) {
        throw new Error('The clinical note was created, but its ID was missing.');
      }

      window.location.href = `/clinical-notes/${clinicalNoteId}`;
    } catch (err) {
      console.error('Clinical profile save / note creation failed:', err);
      setClinicalProfileModal((prev) => ({
        ...prev,
        saving: false,
        error: err.message || 'Clinical profile could not be saved.',
      }));
    }
  };

  const resetCreateForm = () => {
    setPatientId('');
    setPatientName('');
    setSelectedAuthId('');
    setAuthorizations([]);
    setHoldReason('');
    setAssignedDoctorId('');
    setAssignedDoctorName('');
    setSelectedCreateFlagIds([]);
    setRequestedSuperbillDates('');
    setPendingAuthCode('');
  };

  const toggleCreateFlag = (flagId) => {
    const cleanFlagId = Number(flagId);

    setSelectedCreateFlagIds((prev) => {
      if (prev.includes(cleanFlagId)) {
        return prev.filter((id) => id !== cleanFlagId);
      }

      return [...prev, cleanFlagId];
    });
  };

  const createHold = async () => {
    if (!patientId) {
      alert('Please select a patient.');
      return;
    }

    if (!selectedAuthId) {
      alert('Please select the current/old authorization that caused this hold.');
      return;
    }

    const createdByStaffId =
      localStorage.getItem('doctorId') ||
      sessionStorage.getItem('doctorId') ||
      null;

    const selectedCreateFlags = activeFlagDefinitions.filter((flag) =>
      selectedCreateFlagIds.includes(Number(flag.flagId))
    );

    const hasNoAuthNeededFlag = selectedCreateFlags.some(
      (flag) => flag.flagKey === 'NO_AUTHORIZATION_REQUIRED'
    );

    const hasNewEvalFlag = selectedCreateFlags.some(
      (flag) => flag.flagKey === 'EVAL'
    );

    const payload = {
      patientId: parseInt(patientId, 10),
      authId: parseInt(selectedAuthId, 10),
      assignedDoctorId: assignedDoctorId
        ? parseInt(assignedDoctorId, 10)
        : null,
      createdByStaffId: createdByStaffId
        ? parseInt(createdByStaffId, 10)
        : null,
      holdReason: holdReason.trim(),
      noAuthorizationNeeded: hasNoAuthNeededFlag,
      newEval: hasNewEvalFlag,
      requestedSuperbillDates: requestedSuperbillDates.trim(),
      pendingAuthCode: pendingAuthCode.trim(),
    };

    try {
      setCreating(true);
      setError('');

      const createdHold = await service.createFromAuth(payload);
      const newHoldId = createdHold?.holdId;

      if (newHoldId && selectedCreateFlagIds.length > 0) {
        const staffId = getStaffId();

        for (const flagId of selectedCreateFlagIds) {
          await flagService.assignFlagToHold(
            newHoldId,
            flagId,
            staffId ? parseInt(staffId, 10) : null,
            ''
          );
        }
      }

      resetCreateForm();
      setActiveTab('waitingForNotes');
      showSuccess('Authorization hold created.');
      await loadBoard();
    } catch (err) {
      console.error('Failed to create hold:', err);
      alert(err.message || 'Failed to create authorization hold.');
    } finally {
      setCreating(false);
    }
  };

  const startNotes = async (hold) => {
    const doctorId = currentDoctorId || hold.assignedDoctorId;

    if (!doctorId) {
      alert('No doctor ID found. Please assign a doctor first.');
      return;
    }

    try {
      await service.startNotes(hold.holdId, parseInt(doctorId, 10));
      showSuccess('Notes started.');
      await loadBoard();
    } catch (err) {
      alert(err.message || 'Failed to start notes.');
    }
  };

  const markNotesComplete = async (hold) => {
    const doctorId = currentDoctorId || hold.assignedDoctorId;

    if (!doctorId) {
      alert('No doctor ID found.');
      return;
    }

    const notes =
      doctorNotesByHold[hold.holdId] !== undefined
        ? doctorNotesByHold[hold.holdId]
        : hold.doctorNotes || '';

    if (!notes.trim()) {
      const confirmEmpty = window.confirm(
        'Doctor notes are empty. Mark hold notes complete anyway? This will also mark all related superbill notes complete.'
      );

      if (!confirmEmpty) return;
    }

    try {
      await service.markNotesComplete(
        hold.holdId,
        parseInt(doctorId, 10),
        notes
      );

      showSuccess(
        'Hold notes complete. All related superbill notes were also marked complete.'
      );
      await loadBoard();
    } catch (err) {
      alert(err.message || 'Failed to mark hold notes complete.');
    }
  };

  const getClinicalNoteId = (note) => {
    return (
      note?.clinicalNoteId ??
      note?.note?.clinicalNoteId ??
      note?.clinicalNote?.clinicalNoteId ??
      null
    );
  };

  const openClinicalNoteForSuperbill = async (bill, hold = null) => {
    if (!canAccessClinicalNotes) {
      alert('You do not have permission to open clinical notes.');
      return;
    }

    const superbillId = Number(bill?.superbillId);

    if (!Number.isInteger(superbillId) || superbillId <= 0) {
      alert('A valid superbill ID is required to open a clinical note.');
      return;
    }

    if (clinicalNoteLoadingBySuperbill[superbillId]) {
      return;
    }

    setClinicalNoteLoadingBySuperbill((prev) => ({
      ...prev,
      [superbillId]: true,
    }));

    setError('');

    try {
      let note = null;
      const exists = await clinicalNoteService.existsForSuperbill(superbillId);

      if (exists) {
        note = await clinicalNoteService.getClinicalNoteBySuperbill(superbillId);
      } else {
        const confirmed = window.confirm(
          `No clinical note exists for superbill #${superbillId}. Create a Draft clinical note now?`
        );

        if (!confirmed) {
          return;
        }

        try {
          note = await clinicalNoteService.createClinicalNote({ superbillId });
        } catch (createError) {
          /*
           * Recover safely if another staff member created the note
           * between our existence check and the creation request.
           */
          const nowExists = await clinicalNoteService
            .existsForSuperbill(superbillId)
            .catch(() => false);

          if (!nowExists) {
            throw createError;
          }

          note = await clinicalNoteService.getClinicalNoteBySuperbill(superbillId);
        }
      }

      const clinicalNoteId = getClinicalNoteId(note);

      if (!clinicalNoteId) {
        throw new Error('The clinical note was found, but its ID was missing.');
      }

      window.location.href = `/clinical-notes/${clinicalNoteId}`;
    } catch (err) {
      console.error('Clinical note open/create failed:', err);

      if (isClinicalProfileConfigurationError(err)) {
        const authId =
          extractAuthorizationIdFromError(err) ||
          Number(bill?.authId || bill?.authorizationId || hold?.relatedAuthId);

        await openClinicalProfileSetup({
          authId,
          superbillId,
          patientName: hold?.patientName || bill?.patientName || '',
        });
        return;
      }

      alert(err.message || 'Clinical note could not be opened.');
    } finally {
      setClinicalNoteLoadingBySuperbill((prev) => {
        const next = { ...prev };
        delete next[superbillId];
        return next;
      });
    }
  };

  const markSuperbillNotesComplete = async (superbillId) => {
    const staffId =
      currentDoctorId ||
      localStorage.getItem('doctorId') ||
      sessionStorage.getItem('doctorId');

    if (!staffId) {
      alert('No staff/doctor ID found.');
      return;
    }

    try {
      await service.markSuperbillNotesComplete(
        superbillId,
        parseInt(staffId, 10)
      );

      showSuccess('Superbill notes marked complete.');
      await loadBoard();
    } catch (err) {
      alert(err.message || 'Failed to mark superbill notes complete.');
    }
  };

  const sendToAuthorization = async (hold) => {
    const staffId =
      localStorage.getItem('doctorId') ||
      sessionStorage.getItem('doctorId') ||
      null;

    if (!staffId) {
      alert('No staff ID found.');
      return;
    }

    const notes =
      authorizationNotesByHold[hold.holdId] !== undefined
        ? authorizationNotesByHold[hold.holdId]
        : hold.authorizationNotes || '';

    try {
      await service.sendToAuthorization(
        hold.holdId,
        parseInt(staffId, 10),
        notes
      );

      showSuccess('Sent to authorization.');
      await loadBoard();
    } catch (err) {
      alert(err.message || 'Failed to send to authorization.');
    }
  };

  const savePendingAuthCode = async (hold) => {
    const staffId =
      localStorage.getItem('doctorId') ||
      sessionStorage.getItem('doctorId') ||
      null;

    if (!staffId) {
      alert('No staff ID found.');
      return;
    }

    const code =
      pendingAuthCodeByHold[hold.holdId] !== undefined
        ? pendingAuthCodeByHold[hold.holdId]
        : hold.pendingAuthCode || '';

    const notes =
      authorizationNotesByHold[hold.holdId] !== undefined
        ? authorizationNotesByHold[hold.holdId]
        : hold.authorizationNotes || '';

    if (!code.trim()) {
      alert('Pending authorization code is required.');
      return;
    }

    try {
      await service.updatePendingAuthCode(
        hold.holdId,
        parseInt(staffId, 10),
        code.trim(),
        notes
      );

      showSuccess('Pending authorization code saved.');
      await loadBoard();
    } catch (err) {
      alert(err.message || 'Failed to save pending authorization code.');
    }
  };

  const markWaiting = async (hold) => {
    try {
      await service.markWaitingForAuthorization(hold.holdId);
      showSuccess('Marked waiting for authorization.');
      await loadBoard();
    } catch (err) {
      alert(err.message || 'Failed to mark waiting.');
    }
  };

  const enterResult = async (hold) => {
    const staffId =
      localStorage.getItem('doctorId') ||
      sessionStorage.getItem('doctorId') ||
      null;

    if (!staffId) {
      alert('No staff ID found.');
      return;
    }

    const resultStatus = resultStatusByHold[hold.holdId];

    if (!resultStatus) {
      alert('Please choose a result status.');
      return;
    }

    const resultNotes = resultNotesByHold[hold.holdId] || '';

    try {
      await service.enterResult(
        hold.holdId,
        parseInt(staffId, 10),
        resultStatus,
        resultNotes
      );

      showSuccess(`Result saved: ${statusLabel(resultStatus)}`);
      await loadBoard();
    } catch (err) {
      alert(err.message || 'Failed to enter result.');
    }
  };

  const skipNoAuthorizationNeeded = async (hold) => {
    const staffId =
      localStorage.getItem('doctorId') ||
      sessionStorage.getItem('doctorId') ||
      null;

    if (!staffId) {
      alert('No staff ID found.');
      return;
    }

    const resultNotes = noAuthNotesByHold[hold.holdId] || '';

    const confirmed = window.confirm(
      'Close this hold as No Authorization Needed?'
    );

    if (!confirmed) return;

    try {
      await service.skipNoAuthorizationNeeded(
        hold.holdId,
        parseInt(staffId, 10),
        resultNotes
      );

      showSuccess('Hold closed as No Authorization Needed.');
      await loadBoard();
    } catch (err) {
      alert(err.message || 'Failed to close hold.');
    }
  };

  const updateDiagnosisForHold = async (hold) => {
    const doctorId = currentDoctorId || hold.assignedDoctorId;

    if (!doctorId) {
      alert('No doctor ID found.');
      return;
    }

    const diagnosis = getDiagnosisTextForHold(hold);

    if (!diagnosis.trim()) {
      alert('Please choose diagnosis before saving.');
      return;
    }

    try {
      await service.updateDiagnosisForHold(
        hold.holdId,
        parseInt(doctorId, 10),
        diagnosis.trim()
      );

      showSuccess('Diagnosis updated for this hold.');
      await loadBoard();
    } catch (err) {
      alert(err.message || 'Failed to update diagnosis.');
    }
  };

  /**
   * Fetch patient-owned clinical episodes for the optional approval selector.
   * This requires a patient-scoped, authorized backend GET endpoint.
   * A failed lookup never makes episode selection mandatory.
   */
  const loadClinicalEpisodesForPatient = async (patientId, force = false) => {
    const id = Number(patientId);
    if (!Number.isInteger(id) || id <= 0) return;
    if (!force && Object.prototype.hasOwnProperty.call(clinicalEpisodesByPatient, id)) return;
    if (clinicalEpisodeLoadingByPatient[id]) return;

    setClinicalEpisodeLoadingByPatient((prev) => ({ ...prev, [id]: true }));
    setClinicalEpisodeErrorByPatient((prev) => ({ ...prev, [id]: '' }));
    try {
      // Patient-scoped endpoint: GET /clinical-episodes/patient/{patientId}
      // Response does not have to include patientId; the backend scopes the
      // lookup to the requested patient and validates links on approval.
      const response = await authFetch(
        `${apiBaseUrl}/clinical-episodes/patient/${id}`,
        { method: 'GET', headers: { Accept: 'application/json' } }
      );
      const text = await response.text();
      let data = null;
      try { data = text ? JSON.parse(text) : null; } catch { /* malformed JSON */ }
      if (!response.ok) {
        throw new Error(data?.message || data?.error ||
          `Unable to load clinical episodes (${response.status}).`);
      }
      if (!Array.isArray(data)) {
        throw new Error('Clinical episode lookup returned an unexpected response.');
      }
      // The lookup DTO intentionally omits patientId. Previously every
      // valid result was rejected because Number(undefined) is NaN.
      // If a future API includes patientId, reject any mismatched record.
      const patientEpisodes = data.filter((episode) =>
        episode &&
        Number.isSafeInteger(Number(episode.clinicalEpisodeId)) &&
        Number(episode.clinicalEpisodeId) > 0 &&
        (episode.patientId == null || Number(episode.patientId) === id)
      );
      setClinicalEpisodesByPatient((prev) => ({ ...prev, [id]: patientEpisodes }));
    } catch (err) {
      setClinicalEpisodeErrorByPatient((prev) => ({
        ...prev, [id]: err?.message || 'Clinical episodes could not be loaded.',
      }));
    } finally {
      setClinicalEpisodeLoadingByPatient((prev) => ({ ...prev, [id]: false }));
    }
  };

  const createApprovedAuthorizationFromHold = async (hold) => {
    if (approvedAuthSavingByHold[hold.holdId]) return;
    const staffId = getStaffId();
    if (!staffId) {
      alert('No staff ID found.');
      return;
    }
    const form = getApprovedAuthForm(hold);
    if (!form.authCode?.trim()) {
      alert('Approved authorization code is required.');
      return;
    }
    if (!form.startDate || !form.endDate) {
      alert('Authorization start and end dates are required.');
      return;
    }
    if (form.startDate > form.endDate) {
      alert('Start date cannot be after end date.');
      return;
    }
    const totalVisits = Number(form.totalVisits);
    if (!Number.isInteger(totalVisits) || totalVisits <= 0) {
      alert('Total visits must be a positive whole number.');
      return;
    }

    const selectedId = String(form.clinicalEpisodeId || '').trim();
    const clinicalEpisodeId = selectedId ? Number(selectedId) : null;
    if (selectedId) {
      const episodes = clinicalEpisodesByPatient[Number(hold.patientId)];
      const selected = Array.isArray(episodes) && episodes.find((episode) =>
        Number(episode.clinicalEpisodeId) === clinicalEpisodeId
      );
      if (!Number.isSafeInteger(clinicalEpisodeId) || clinicalEpisodeId <= 0 ||
          !selected || String(selected.status).toUpperCase() !== 'ACTIVE') {
        alert('Choose a valid ACTIVE clinical episode belonging to this patient.');
        return;
      }
    }

    const wording = clinicalEpisodeId
      ? ` and link it to existing clinical episode #${clinicalEpisodeId}`
      : ' without linking a clinical episode';
    if (!window.confirm(
      `Create approved authorization "${form.authCode.trim()}"${wording} and close this hold?`
    )) return;

    const payload = {
      staffId: Number(staffId),
      authCode: form.authCode.trim(),
      startDate: form.startDate,
      endDate: form.endDate,
      totalVisits,
      active: form.active || 'yes',
      referredBy: form.referredBy || '',
      resultNotes: form.resultNotes || '',
      diagnosisSet: form.diagnosisSet || '',
      procedureSet: form.procedureSet || '',
      clinicalEpisodeId,
    };

    setApprovedAuthSavingByHold((prev) => ({ ...prev, [hold.holdId]: true }));
    try {
      const createdHold = await service.createApprovedAuthorizationFromHold(
        hold.holdId, payload
      );
      const approvedAuthId = Number(createdHold?.approvedAuthId);
      if (!Number.isInteger(approvedAuthId) || approvedAuthId <= 0) {
        // The server may already have committed. Do not retry blindly.
        await loadBoard();
        throw new Error('Approval response did not include the authorization ID. Check Results before retrying.');
      }
      // Do NOT create a separate authorization clinical profile here.
      // The selected episode is linked atomically by the backend. When
      // unlinked, the existing Initial Evaluation/profile workflow applies.
      showSuccess(clinicalEpisodeId
        ? 'Approved authorization linked to the existing clinical episode. Hold closed.'
        : 'Approved authorization created without an episode. Hold closed.');
      await loadBoard();
    } catch (err) {
      alert(err?.message || 'Failed to create approved authorization.');
    } finally {
      setApprovedAuthSavingByHold((prev) => ({ ...prev, [hold.holdId]: false }));
    }
  };

  const deleteHold = async (hold) => {
    if (!window.confirm('Delete this authorization hold?')) {
      return;
    }

    try {
      await service.deleteHold(hold.holdId);
      showSuccess('Authorization hold deleted.');
      await loadBoard();
    } catch (err) {
      alert(err.message || 'Failed to delete hold.');
    }
  };


  const getWorkflowNote = (holdId) => {
    return workflowNotesByHold[holdId] || '';
  };

  const setWorkflowNote = (holdId, value) => {
    setWorkflowNotesByHold((prev) => ({
      ...prev,
      [holdId]: value,
    }));
  };

  const putHoldOnHold = async (hold) => {
    const staffId = getStaffId();

    if (!staffId) {
      alert('No staff ID found.');
      return;
    }

    const notes = getWorkflowNote(hold.holdId);

    if (!notes.trim()) {
      const confirmed = window.confirm(
        'Put this hold on hold without adding a reason?'
      );

      if (!confirmed) return;
    }

    try {
      await service.putOnHold(hold.holdId, parseInt(staffId, 10), notes);
      showSuccess('Hold moved to On Hold.');
      closeExpandedSectionsForHold(hold.holdId);
      await loadBoard();
      await updateFocusedHoldAfterRefresh(hold.holdId);
      setFocusedSectionKey('onHold');
    } catch (err) {
      alert(err.message || 'Failed to put hold on hold.');
    }
  };

  const resumeHold = async (hold) => {
    const staffId = getStaffId();

    if (!staffId) {
      alert('No staff ID found.');
      return;
    }

    const notes = getWorkflowNote(hold.holdId);

    try {
      await service.resumeHold(hold.holdId, parseInt(staffId, 10), notes);
      showSuccess('Hold resumed.');
      closeExpandedSectionsForHold(hold.holdId);
      await loadBoard();
      await updateFocusedHoldAfterRefresh(hold.holdId);
      setFocusedSectionKey('');
    } catch (err) {
      alert(err.message || 'Failed to resume hold.');
    }
  };

  const backtrackHold = async (hold) => {
    const staffId = getStaffId();

    if (!staffId) {
      alert('No staff ID found.');
      return;
    }

    const confirmed = window.confirm(
      'Move this hold back one workflow step?'
    );

    if (!confirmed) return;

    const notes = getWorkflowNote(hold.holdId);

    try {
      await service.backtrackHold(hold.holdId, parseInt(staffId, 10), notes);
      showSuccess('Hold moved back one workflow step.');
      closeExpandedSectionsForHold(hold.holdId);
      await loadBoard();
      await updateFocusedHoldAfterRefresh(hold.holdId);
      setFocusedSectionKey('');
    } catch (err) {
      alert(err.message || 'Failed to move hold back.');
    }
  };

  const toggleHoldSection = (holdOrId, sectionName) => {
    const hold = typeof holdOrId === 'object' ? holdOrId : null;
    const holdId = hold?.holdId || holdOrId;
    const willOpen = !expandedHoldSections[holdId]?.[sectionName];

    setExpandedHoldSections((prev) => ({
      ...prev,
      [holdId]: {
        ...(prev[holdId] || {}),
        [sectionName]: !prev[holdId]?.[sectionName],
      },
    }));

    if (sectionName === 'files' && willOpen && hold) {
      loadPatientFilesForHold(hold);
    }
    if (sectionName === 'newAuthorization' && willOpen && hold?.patientId) {
      void loadClinicalEpisodesForPatient(hold.patientId);
    }
  };

  const isHoldSectionExpanded = (holdId, sectionName) => {
    return !!expandedHoldSections[holdId]?.[sectionName];
  };

  const toggleActionSection = (holdId, sectionName) => {
    setExpandedActionSections((prev) => ({
      ...prev,
      [holdId]: {
        ...(prev[holdId] || {}),
        [sectionName]: !prev[holdId]?.[sectionName],
      },
    }));
  };

  const isActionSectionExpanded = (holdId, sectionName) => {
    return !!expandedActionSections[holdId]?.[sectionName];
  };

  const renderActionAccordionSection = (
    hold,
    sectionName,
    title,
    description,
    content,
    defaultOpen = false
  ) => {
    if (!content) {
      return null;
    }

    const hasStoredState =
      expandedActionSections[hold.holdId] &&
      Object.prototype.hasOwnProperty.call(
        expandedActionSections[hold.holdId],
        sectionName
      );

    const open = hasStoredState
      ? isActionSectionExpanded(hold.holdId, sectionName)
      : defaultOpen;

    return (
      <div className={`hold-action-accordion ${open ? 'open' : ''}`}>
        <button
          type="button"
          className="hold-action-accordion-header"
          onClick={() => toggleActionSection(hold.holdId, sectionName)}
          aria-expanded={open}
        >
          <span className="hold-action-accordion-arrow">
            {open ? '▾' : '▸'}
          </span>

          <span className="hold-action-accordion-title-wrap">
            <strong>{title}</strong>
            {description && <small>{description}</small>}
          </span>
        </button>

        {open && <div className="hold-action-accordion-body">{content}</div>}
      </div>
    );
  };

  const toggleSuperbillExpand = (superbillId) => {
    setExpandedSuperbills((prev) => ({
      ...prev,
      [superbillId]: !prev[superbillId],
    }));
  };

  const showAllSuperbillsForHold = (holdId, totalCount) => {
    setVisibleSuperbillLimitByHold((prev) => ({
      ...prev,
      [holdId]: totalCount,
    }));
  };

  const showLessSuperbillsForHold = (holdId) => {
    setVisibleSuperbillLimitByHold((prev) => ({
      ...prev,
      [holdId]: 4,
    }));
  };

  const formatDate = (value) => {
    if (!value) return 'N/A';

    const parts = String(value).split('-');

    if (parts.length !== 3) {
      return value;
    }

    const [year, month, day] = parts;
    return `${month}-${day}-${year}`;
  };

  const formatDateTime = (value) => {
    if (!value) return 'N/A';

    try {
      return new Date(value).toLocaleString();
    } catch {
      return value;
    }
  };

  const statusLabel = (status) => {
    return String(status || 'UNKNOWN').replaceAll('_', ' ');
  };

  const formatTypeVisit = (typeVisit) => {
    if (!typeVisit) return 'N/A';

    const normalized = String(typeVisit).trim().toLowerCase();

    if (normalized === 'progress note') return 'PN';
    if (normalized === 'discharge') return 'DIS';

    return typeVisit;
  };

  const formatCommaList = (value) => {
    const pieces = parseCommaCodes(value);

    if (pieces.length === 0) {
      return <i>None</i>;
    }

    return pieces.map((item, index) => <li key={`${item}-${index}`}>{item}</li>);
  };

  const escapeHtml = (value) => {
    return String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  };

  const formatExportList = (value) => {
    const pieces = parseCommaCodes(value);

    if (pieces.length === 0) {
      return '<p>N/A</p>';
    }

    return `
      <ul>
        ${pieces.map((item) => `<li>${escapeHtml(item)}</li>`).join('')}
      </ul>
    `;
  };

  const exportSingleHold = (hold) => {
    const superbills = Array.isArray(hold.superbills) ? hold.superbills : [];

    const superbillRows =
      superbills.length > 0
        ? superbills
            .map(
              (bill) => `
                <tr>
                  <td>${escapeHtml(formatDate(bill.superbillDate))}</td>
                  <td>${escapeHtml(bill.superbillId || 'N/A')}</td>
                  <td>${escapeHtml(bill.appointmentId || 'N/A')}</td>
                  <td>${escapeHtml(formatTypeVisit(bill.typeVisit))}</td>
                  <td>${escapeHtml(
                    bill.notesCompleted ? 'Complete' : 'Needed'
                  )}</td>
                  <td>${escapeHtml(bill.billed ? 'Yes' : 'No')}</td>
                  <td>${escapeHtml(bill.notesCompletedByName || 'N/A')}</td>
                </tr>

                <tr>
                  <td colspan="7">
                    <strong>Diagnosis:</strong>
                    ${formatExportList(bill.diagnosisSet)}

                    <strong>Procedure:</strong>
                    ${formatExportList(bill.procedureSet)}

                    <strong>Appointment Notes:</strong>
                    <p>${escapeHtml(bill.appointmentNotes || 'N/A')}</p>
                  </td>
                </tr>
              `
            )
            .join('')
        : `
            <tr>
              <td colspan="7">No related superbills found.</td>
            </tr>
          `;

    const printWindow = window.open('', '_blank');

    if (!printWindow) {
      alert('Popup blocked. Please allow popups to export this hold.');
      return;
    }

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Authorization Hold #${escapeHtml(hold.holdId)}</title>

          <style>
            body {
              font-family: Arial, sans-serif;
              color: #111827;
              padding: 24px;
              line-height: 1.4;
            }

            h1 {
              text-align: center;
              margin-bottom: 6px;
            }

            h2 {
              margin-top: 24px;
              padding-bottom: 6px;
              border-bottom: 1px solid #d1d5db;
            }

            .summary {
              text-align: center;
              color: #4b5563;
              margin-bottom: 20px;
            }

            .grid {
              display: grid;
              grid-template-columns: repeat(2, minmax(0, 1fr));
              gap: 10px 18px;
            }

            .field {
              padding: 8px;
              border: 1px solid #e5e7eb;
              border-radius: 8px;
              background: #f9fafb;
            }

            .label {
              font-weight: bold;
              color: #374151;
              display: block;
              margin-bottom: 3px;
            }

            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 10px;
            }

            th {
              background: #1f2937;
              color: white;
            }

            th, td {
              border: 1px solid #d1d5db;
              padding: 8px;
              text-align: left;
              vertical-align: top;
              font-size: 12px;
            }

            ul {
              margin-top: 4px;
              margin-bottom: 12px;
            }

            .notes-box {
              border: 1px solid #e5e7eb;
              border-radius: 8px;
              padding: 10px;
              background: #f9fafb;
              white-space: pre-wrap;
            }

            @media print {
              body {
                padding: 0;
              }

              .no-print {
                display: none;
              }

              .field {
                break-inside: avoid;
              }

              tr {
                page-break-inside: avoid;
              }
            }
          </style>
        </head>

        <body>
          <button class="no-print" onclick="window.print()" style="margin-bottom: 16px;">
            Print / Save PDF
          </button>

          <h1>Authorization Hold Report</h1>

          <div class="summary">
            Hold #${escapeHtml(hold.holdId)} |
            Status: ${escapeHtml(statusLabel(hold.status))} |
            Exported: ${escapeHtml(new Date().toLocaleString())}
          </div>

          <h2>Patient / Authorization</h2>

          <div class="grid">
            <div class="field">
              <span class="label">Patient</span>
              ${escapeHtml(hold.patientName || `Patient #${hold.patientId}`)}
            </div>

            <div class="field">
              <span class="label">DOB</span>
              ${escapeHtml(formatDate(hold.patientDateOfBirth))}
            </div>

            <div class="field">
              <span class="label">Insurance</span>
              ${escapeHtml(hold.insuranceName || 'N/A')}
            </div>

            <div class="field">
              <span class="label">Subscriber ID</span>
              ${escapeHtml(hold.subscriberId || 'N/A')}
            </div>

            <div class="field">
              <span class="label">Old Authorization Code</span>
              ${escapeHtml(hold.authCode || 'N/A')}
            </div>

            <div class="field">
              <span class="label">Pending New Auth Code</span>
              ${escapeHtml(hold.pendingAuthCode || 'N/A')}
            </div>

            <div class="field">
              <span class="label">Approved Authorization Code</span>
              ${escapeHtml(hold.approvedAuthCode || 'N/A')}
            </div>

            <div class="field">
              <span class="label">Assigned Doctor</span>
              ${escapeHtml(hold.assignedDoctorName || 'N/A')}
            </div>

            <div class="field">
              <span class="label">Auth Dates</span>
              ${escapeHtml(formatDate(hold.authStartDate))} to ${escapeHtml(
      formatDate(hold.authEndDate)
    )}
            </div>

            <div class="field">
              <span class="label">Visits</span>
              Remaining: ${escapeHtml(hold.remainingVisits ?? 'N/A')} /
              Total: ${escapeHtml(hold.totalVisits ?? 'N/A')}
            </div>
          </div>

          <h2>Hold Reason</h2>
          <div class="notes-box">${escapeHtml(hold.holdReason || 'N/A')}</div>

          <h2>Diagnosis / Procedure Summary</h2>

          <strong>Diagnosis</strong>
          ${formatExportList(hold.diagnosisSet)}

          <strong>Updated Diagnosis</strong>
          ${formatExportList(hold.updatedDiagnosisSet)}

          <strong>Procedure</strong>
          ${formatExportList(hold.procedureSet)}

          <h2>Related Superbills</h2>

          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Superbill ID</th>
                <th>Appointment ID</th>
                <th>Type Visit</th>
                <th>Notes</th>
                <th>Billed</th>
                <th>Notes Completed By</th>
              </tr>
            </thead>

            <tbody>
              ${superbillRows}
            </tbody>
          </table>

          <h2>Doctor Notes</h2>
          <div class="notes-box">${escapeHtml(hold.doctorNotes || 'N/A')}</div>

          <h2>Authorization Submission Notes</h2>
          <div class="notes-box">${escapeHtml(
            hold.authorizationNotes || 'N/A'
          )}</div>

          <h2>Result Notes</h2>
          <div class="notes-box">${escapeHtml(hold.resultNotes || 'N/A')}</div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
  };

  const getStatusClass = (status) => {
    const normalized = String(status || '').toLowerCase();

    if (normalized.includes('approved')) return 'hold-status approved';
    if (normalized.includes('denied')) return 'hold-status denied';
    if (normalized.includes('cancelled')) return 'hold-status cancelled';
    if (normalized.includes('notes')) return 'hold-status notes';
    if (normalized.includes('authorization')) return 'hold-status auth';
    if (normalized.includes('hold')) return 'hold-status on-hold';

    return 'hold-status';
  };

  const getAuthLabel = (auth) => {
    const authId = auth.authId || auth.id;
    const code = auth.authCode || 'No Code';
    const total = auth.totalVisits ?? 'N/A';
    const remaining = auth.visits ?? 'N/A';
    const active = auth.active || 'N/A';

    return `${code} | Auth #${authId} | Total: ${total} | Remaining: ${remaining} | Active: ${active}`;
  };

  const selectedAuth = useMemo(() => {
    return authorizations.find(
      (auth) => String(auth.authId || auth.id) === String(selectedAuthId)
    );
  }, [authorizations, selectedAuthId]);

  const authorizationHolds = useMemo(() => {
    return [
      ...(board.sentToAuthorization || []),
      ...(board.waitingForAuthorization || []),
    ];
  }, [board]);

  const activeTabCount = useMemo(() => {
    if (activeTab === 'create') return 0;
    if (activeTab === 'authorization') return authorizationHolds.length;
    if (activeTab === 'results') return resultsData.totalElements;
    return board[activeTab]?.length || 0;
  }, [activeTab, authorizationHolds, board, resultsData.totalElements]);

  const allHoldsCount = useMemo(() => {
    return (
      (board.waitingForNotes?.length || 0) +
      (board.notesComplete?.length || 0) +
      (board.sentToAuthorization?.length || 0) +
      (board.waitingForAuthorization?.length || 0) +
      (board.onHold?.length || 0)
    );
  }, [board]);

  const allHoldsForSearch = useMemo(() => {
    return [
      ...(board.waitingForNotes || []).map((hold) => ({
        hold,
        sectionKey: 'waitingForNotes',
      })),
      ...(board.notesComplete || []).map((hold) => ({
        hold,
        sectionKey: 'notesComplete',
      })),
      ...(board.sentToAuthorization || []).map((hold) => ({
        hold,
        sectionKey: 'authorization',
      })),
      ...(board.waitingForAuthorization || []).map((hold) => ({
        hold,
        sectionKey: 'authorization',
      })),
      ...(board.onHold || []).map((hold) => ({
        hold,
        sectionKey: 'onHold',
      })),
    ];
  }, [board]);

  const normalizedPatientSearchTerm = patientSearchTerm.trim().toLowerCase();

  const searchMatchedHolds = useMemo(() => {
    if (!normalizedPatientSearchTerm) {
      return [];
    }

    return allHoldsForSearch.filter(({ hold }) => {
      const searchableText = [
        hold.patientName,
        hold.firstName,
        hold.lastName,
        hold.patientId,
        hold.holdId,
        hold.authCode,
        hold.pendingAuthCode,
        hold.approvedAuthCode,
        hold.insuranceName,
        hold.subscriberId,
        statusLabel(hold.status),
        Array.isArray(hold.flags)
          ? hold.flags.map((flag) => flag.label || flag.flagKey).join(' ')
          : '',
      ]
        .filter((value) => value !== null && value !== undefined)
        .join(' ')
        .toLowerCase();

      return searchableText.includes(normalizedPatientSearchTerm);
    });
  }, [allHoldsForSearch, normalizedPatientSearchTerm]);

  const getSectionKeyForHold = (hold) => {
    const status = String(hold?.status || '').toUpperCase();

    if (status === 'WAITING_FOR_NOTES') return 'waitingForNotes';
    if (status === 'NOTES_COMPLETE') return 'notesComplete';
    if (status === 'SENT_TO_AUTHORIZATION') return 'authorization';
    if (status === 'WAITING_FOR_AUTHORIZATION') return 'authorization';
    if (status === 'ON_HOLD') return 'onHold';

    return 'results';
  };

  const getHoldNotePreview = (hold) => {
    return (
      hold.onHoldNotes ||
      hold.holdReason ||
      hold.doctorNotes ||
      hold.authorizationNotes ||
      hold.resultNotes ||
      'No notes added.'
    );
  };

  const renderInfoItem = (label, value) => {
    return (
      <div className="hold-compact-info-item">
        <span>{label}</span>
        <strong>{value || 'N/A'}</strong>
      </div>
    );
  };

  const renderCodeChips = ({
    codes,
    emptyText,
    chipClassName = 'hold-diagnosis-chip',
    removeClassName = 'hold-diagnosis-chip-remove',
    onRemove,
  }) => {
    const codeList = parseCommaCodes(codes);

    return (
      <div className="hold-diagnosis-chip-area">
        {codeList.length === 0 ? (
          <p className="hold-small">{emptyText}</p>
        ) : (
          codeList.map((code) => (
            <span key={code} className={chipClassName}>
              <span>{code}</span>

              {onRemove && (
                <button
                  type="button"
                  className={removeClassName}
                  onClick={() => onRemove(code)}
                  aria-label={`Remove ${code}`}
                  title={`Remove ${code}`}
                >
                  <span aria-hidden="true">×</span>
                </button>
              )}
            </span>
          ))
        )}
      </div>
    );
  };

  const getHoldFlags = (hold) => {
    return Array.isArray(hold?.flags) ? hold.flags : [];
  };

  const getFlagCountForHold = (hold) => {
    return getHoldFlags(hold).length;
  };

  const getFlagSummaryText = (hold) => {
    const flags = getHoldFlags(hold);

    if (flags.length === 0) {
      return 'No flags';
    }

    return flags.map((flag) => flag.label || flag.flagKey || 'Flag').join(', ');
  };

  const assignFlagToHold = async (hold) => {
    if (!canManageAuth) {
      alert('You do not have permission to assign flags.');
      return;
    }

    const staffId = getStaffId();

    if (!staffId) {
      alert('No staff ID found.');
      return;
    }

    const flagId = flagSelectionByHold[hold.holdId];

    if (!flagId) {
      alert('Please select a flag.');
      return;
    }

    const note = flagNotesByHold[hold.holdId] || '';

    try {
      await flagService.assignFlagToHold(
        hold.holdId,
        parseInt(flagId, 10),
        parseInt(staffId, 10),
        note
      );

      setFlagSelectionByHold((prev) => ({ ...prev, [hold.holdId]: '' }));
      setFlagNotesByHold((prev) => ({ ...prev, [hold.holdId]: '' }));

      showSuccess('Flag added to hold.');
      await loadBoard();
      await updateFocusedHoldAfterRefresh(hold.holdId);
    } catch (err) {
      alert(err.message || 'Failed to add flag.');
    }
  };

  const removeFlagFromHold = async (hold, flag) => {
    if (!canManageAuth) {
      alert('You do not have permission to remove flags.');
      return;
    }

    const label = flag.label || flag.flagKey || 'this flag';
    const confirmed = window.confirm(`Remove "${label}" from this hold?`);

    if (!confirmed) {
      return;
    }

    try {
      await flagService.removeFlagFromHold(hold.holdId, flag.flagId);
      showSuccess('Flag removed from hold.');
      await loadBoard();
      await updateFocusedHoldAfterRefresh(hold.holdId);
    } catch (err) {
      alert(err.message || 'Failed to remove flag.');
    }
  };

  const loadPatientFilesForHold = async (hold, force = false) => {
    if (!hold?.holdId || !hold?.patientId) {
      return;
    }

    if (!force && patientFilesByHold[hold.holdId]) {
      return;
    }

    try {
      setPatientFilesLoadingByHold((prev) => ({
        ...prev,
        [hold.holdId]: true,
      }));

      const [files, usage] = await Promise.all([
        patientFileService.listByPatientId(hold.patientId),
        patientFileService.getUsageByPatientId(hold.patientId).catch(() => null),
      ]);

      setPatientFilesByHold((prev) => ({
        ...prev,
        [hold.holdId]: Array.isArray(files) ? files : [],
      }));

      setPatientFileUsageByHold((prev) => ({
        ...prev,
        [hold.holdId]: usage,
      }));
    } catch (err) {
      alert(err.message || 'Failed to load patient files.');
    } finally {
      setPatientFilesLoadingByHold((prev) => ({
        ...prev,
        [hold.holdId]: false,
      }));
    }
  };

  const uploadPatientFileForHold = async (hold) => {
    const file = patientFileUploadByHold[hold.holdId];

    if (!file) {
      alert('Please choose a file first.');
      return;
    }

    try {
      await patientFileService.uploadFile({
        patientId: hold.patientId,
        file,
        filename:
          String(patientFileCustomNameByHold[hold.holdId] || '').trim() ||
          file.name ||
          '',
      });

      setPatientFileUploadByHold((prev) => ({ ...prev, [hold.holdId]: null }));
      setPatientFileCustomNameByHold((prev) => ({ ...prev, [hold.holdId]: '' }));

      const input = document.getElementById(`hold-file-upload-${hold.holdId}`);
      if (input) {
        input.value = '';
      }

      showSuccess('Patient file uploaded.');
      await loadPatientFilesForHold(hold, true);
    } catch (err) {
      alert(err.message || 'Failed to upload patient file.');
    }
  };

  const getPatientFileName = (file) => {
    const candidates = [
      file?.displayName,
      file?.display_name,
      file?.originalFilename,
      file?.originalFileName,
      file?.original_name,
      file?.originalName,
      file?.fileName,
      file?.name,
      file?.filename,
      file?.storedFilename,
      file?.storedFileName,
    ];

    const meaningfulName = candidates.find((candidate) => {
      const clean = String(candidate || '').trim();

      return (
        clean &&
        clean.toLowerCase() !== 'patient file' &&
        clean.toLowerCase() !== 'patient_file'
      );
    });

    return meaningfulName || candidates.find((candidate) => String(candidate || '').trim()) || 'Patient File';
  };

  const getPatientFilePublicId = (file) => {
    return file?.publicId || file?.public_id || file?.id;
  };

  const formatBytes = (bytes) => {
    const value = Number(bytes || 0);

    if (!value) {
      return 'N/A';
    }

    if (value < 1024) {
      return `${value} B`;
    }

    if (value < 1024 * 1024) {
      return `${(value / 1024).toFixed(1)} KB`;
    }

    return `${(value / 1024 / 1024).toFixed(1)} MB`;
  };

  const getPatientFileSize = (file) => {
    return file?.sizeBytes || file?.fileSize || file?.size || file?.bytes;
  };

  const previewPatientFile = async (file) => {
    const publicId = getPatientFilePublicId(file);

    if (!publicId) {
      alert('Missing file ID.');
      return;
    }

    try {
      const blob = await patientFileService.downloadBlob(publicId, {
        attachment: false,
      });

      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (err) {
      alert(err.message || 'Failed to open file.');
    }
  };

  const downloadPatientFile = async (file) => {
    const publicId = getPatientFilePublicId(file);

    if (!publicId) {
      alert('Missing file ID.');
      return;
    }

    try {
      await patientFileService.forceDownload(publicId, getPatientFileName(file), {
        attachment: true,
      });
    } catch (err) {
      alert(err.message || 'Failed to download file.');
    }
  };

  const renamePatientFile = async (hold, file) => {
    const publicId = getPatientFilePublicId(file);

    if (!publicId) {
      alert('Missing file ID.');
      return;
    }

    const currentName = getPatientFileName(file);
    const newName = window.prompt('New file name:', currentName);

    if (!newName || !newName.trim() || newName.trim() === currentName) {
      return;
    }

    try {
      await patientFileService.rename(publicId, newName.trim());
      showSuccess('Patient file renamed.');
      await loadPatientFilesForHold(hold, true);
    } catch (err) {
      alert(err.message || 'Failed to rename file.');
    }
  };

  const deletePatientFile = async (hold, file) => {
    const publicId = getPatientFilePublicId(file);

    if (!publicId) {
      alert('Missing file ID.');
      return;
    }

    const confirmed = window.confirm(`Delete "${getPatientFileName(file)}"?`);

    if (!confirmed) {
      return;
    }

    try {
      await patientFileService.delete(publicId);
      showSuccess('Patient file deleted.');
      await loadPatientFilesForHold(hold, true);
    } catch (err) {
      alert(err.message || 'Failed to delete file.');
    }
  };

  const renderFlagChips = (hold, { removable = false } = {}) => {
    const flags = getHoldFlags(hold);

    if (flags.length === 0) {
      return <p className="hold-small">No flags selected.</p>;
    }

    return (
      <div className="hold-flag-chip-list">
        {flags.map((flag) => (
          <span
            key={`${flag.flagId}-${flag.assignmentId || flag.flagKey}`}
            className={`hold-flag-chip hold-flag-${flag.color || 'none'}`}
            title={flag.description || flag.flagKey || ''}
          >
            <span>{flag.label || flag.flagKey || 'Flag'}</span>

            {removable && canManageAuth && (
              <button
                type="button"
                onClick={() => removeFlagFromHold(hold, flag)}
                aria-label={`Remove ${flag.label || 'flag'}`}
                title="Remove flag"
              >
                ×
              </button>
            )}
          </span>
        ))}
      </div>
    );
  };

  const renderFlagPreview = (hold) => {
    const flags = getHoldFlags(hold);

    if (flags.length === 0) {
      return (
        <div className="hold-card-flag-preview empty">
          <strong>Flags:</strong>
          <span>No flags selected.</span>
        </div>
      );
    }

    return (
      <div className="hold-card-flag-preview">
        <strong>Flags:</strong>
        {renderFlagChips(hold)}
      </div>
    );
  };

  const renderFlagsPanel = (hold) => {
    return (
      <div className="hold-section hold-flags-management-section">
        <strong>Hold Flags</strong>

        <p className="hold-small">
          Add labels such as Needs Referral, Progress Note, Re-Eval, or Allow
          Superbill While On Hold.
        </p>

        {renderFlagChips(hold, { removable: true })}

        {canManageAuth && (
          <div className="hold-flag-add-row">
            <select
              value={flagSelectionByHold[hold.holdId] || ''}
              onChange={(e) =>
                setFlagSelectionByHold((prev) => ({
                  ...prev,
                  [hold.holdId]: e.target.value,
                }))
              }
            >
              <option value="">Select flag</option>

              {activeFlagDefinitions.map((flag) => (
                <option key={flag.flagId} value={flag.flagId}>
                  {flag.label || flag.flagKey}
                </option>
              ))}
            </select>

            <input
              type="text"
              value={flagNotesByHold[hold.holdId] || ''}
              onChange={(e) =>
                setFlagNotesByHold((prev) => ({
                  ...prev,
                  [hold.holdId]: e.target.value,
                }))
              }
              placeholder="Optional flag note"
            />

            <button
              type="button"
              className="hold-btn-save"
              onClick={() => assignFlagToHold(hold)}
            >
              Add Flag
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderPatientFilesPanel = (hold) => {
    const files = patientFilesByHold[hold.holdId] || [];
    const usage = patientFileUsageByHold[hold.holdId];
    const loadingFiles = !!patientFilesLoadingByHold[hold.holdId];

    return (
      <div className="hold-collapsible-panel hold-files-panel">
        <div className="hold-panel-title">Patient Files</div>

        <div className="hold-files-top-row">
          <p className="hold-small">
            These are the patient directory files for{' '}
            <strong>{hold.patientName || `Patient #${hold.patientId}`}</strong>.
          </p>

          <button
            type="button"
            className="hold-mini-link-button"
            onClick={() => loadPatientFilesForHold(hold, true)}
          >
            Refresh Files
          </button>
        </div>

        {usage && (
          <div className="hold-files-usage-box">
            Storage Used:{' '}
            <strong>
              {formatBytes(
                usage.usedBytes || usage.totalBytesUsed || usage.used || usage.bytesUsed
              )}
            </strong>
          </div>
        )}

        {canManageAuth && (
          <div className="hold-file-upload-box">
            <input
              id={`hold-file-upload-${hold.holdId}`}
              type="file"
              onChange={(e) =>
                setPatientFileUploadByHold((prev) => ({
                  ...prev,
                  [hold.holdId]: e.target.files?.[0] || null,
                }))
              }
            />

            <input
              type="text"
              value={patientFileCustomNameByHold[hold.holdId] || ''}
              onChange={(e) =>
                setPatientFileCustomNameByHold((prev) => ({
                  ...prev,
                  [hold.holdId]: e.target.value,
                }))
              }
              placeholder="Optional display filename"
            />

            <button
              type="button"
              className="hold-btn-save"
              onClick={() => uploadPatientFileForHold(hold)}
            >
              Upload File
            </button>
          </div>
        )}

        {loadingFiles ? (
          <p className="hold-loading-text">Loading patient files...</p>
        ) : files.length === 0 ? (
          <div className="hold-empty-small">No files found for this patient.</div>
        ) : (
          <div className="hold-file-list">
            {files.map((file) => {
              const publicId = getPatientFilePublicId(file);

              return (
                <div className="hold-file-row" key={publicId || getPatientFileName(file)}>
                  <div className="hold-file-main">
                    <strong>{getPatientFileName(file)}</strong>

                    <span>
                      {file.contentType || file.mimeType || 'File'} |{' '}
                      {formatBytes(getPatientFileSize(file))}
                    </span>
                  </div>

                  <div className="hold-file-actions">
                    <button type="button" onClick={() => previewPatientFile(file)}>
                      View
                    </button>

                    <button type="button" onClick={() => downloadPatientFile(file)}>
                      Download
                    </button>

                    {canManageAuth && (
                      <>
                        <button type="button" onClick={() => renamePatientFile(hold, file)}>
                          Rename
                        </button>

                        <button
                          type="button"
                          className="hold-file-delete-btn"
                          onClick={() => deletePatientFile(hold, file)}
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderSuperbillList = (hold) => {
    const superbills = Array.isArray(hold.superbills) ? hold.superbills : [];
    const visibleLimit = visibleSuperbillLimitByHold[hold.holdId] || 4;
    const visibleSuperbills = superbills.slice(0, visibleLimit);

    if (superbills.length === 0) {
      return <p>No related superbills found for this authorization.</p>;
    }

    return (
      <div className="hold-superbill-list">
        <div className="hold-superbill-summary-row">
          <strong>{superbills.length} related superbill date(s)</strong>

          {superbills.length > 4 && (
            <button
              type="button"
              className="hold-mini-link-button"
              onClick={() =>
                visibleLimit >= superbills.length
                  ? showLessSuperbillsForHold(hold.holdId)
                  : showAllSuperbillsForHold(hold.holdId, superbills.length)
              }
            >
              {visibleLimit >= superbills.length ? 'Show Less' : 'Show All'}
            </button>
          )}
        </div>

        {visibleSuperbills.map((bill) => {
          const isBillExpanded = !!expandedSuperbills[bill.superbillId];

          return (
            <div className="hold-superbill-row compact" key={bill.superbillId}>
              <div className="hold-superbill-main-line">
                <div>
                  <strong>{formatDate(bill.superbillDate)}</strong>

                  <div className="hold-small">
                    Type: {formatTypeVisit(bill.typeVisit)} | Superbill #
                    {bill.superbillId}
                  </div>
                </div>

                <div className="hold-superbill-statuses">
                  <span
                    className={
                      bill.notesCompleted
                        ? 'hold-mini-status complete'
                        : 'hold-mini-status incomplete'
                    }
                  >
                    Notes: {bill.notesCompleted ? 'Complete' : 'Needed'}
                  </span>

                  <span
                    className={
                      bill.billed
                        ? 'hold-mini-status complete'
                        : 'hold-mini-status incomplete'
                    }
                  >
                    Billed: {bill.billed ? 'Yes' : 'No'}
                  </span>
                </div>

                <div className="hold-superbill-actions hold-action-grid compact-grid">
                  <button
                    type="button"
                    onClick={() => toggleSuperbillExpand(bill.superbillId)}
                  >
                    {isBillExpanded ? 'Hide' : 'Details'}
                  </button>

                  {canAccessClinicalNotes && (
                    <button
                      type="button"
                      className="hold-btn-save"
                      disabled={!!clinicalNoteLoadingBySuperbill[bill.superbillId]}
                      onClick={() => openClinicalNoteForSuperbill(bill, hold)}
                    >
                      {clinicalNoteLoadingBySuperbill[bill.superbillId]
                        ? 'Opening...'
                        : 'Clinical Note'}
                    </button>
                  )}

                  {!bill.notesCompleted && canDoctorNotes && (
                    <button
                      type="button"
                      className="hold-btn-save"
                      onClick={() =>
                        markSuperbillNotesComplete(bill.superbillId)
                      }
                    >
                      Notes Done
                    </button>
                  )}
                </div>
              </div>

              {isBillExpanded && (
                <div className="hold-superbill-clinical">
                  <div className="hold-compact-info-grid">
                    {renderInfoItem('Appointment ID', bill.appointmentId)}
                    {renderInfoItem(
                      'Diagnosis/Procedure Record',
                      bill.diagCombId
                    )}
                    {renderInfoItem(
                      'Notes Completed By',
                      bill.notesCompletedByName || 'N/A'
                    )}
                  </div>

                  <strong>Diagnosis</strong>
                  <ul>{formatCommaList(bill.diagnosisSet)}</ul>

                  <strong>Procedure</strong>
                  <ul>{formatCommaList(bill.procedureSet)}</ul>

                  <strong>Appointment Notes</strong>
                  <p>{bill.appointmentNotes || 'N/A'}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderCreateTab = () => {
    return (
      <div className="hold-create-card">
        <h2>Create Authorization Hold</h2>

        <div className="hold-create-grid">
          <div>
            <button type="button" onClick={openPatientSearch}>
              Search Patient
            </button>

            <input
              type="text"
              value={patientName}
              readOnly
              placeholder="Selected Patient"
            />

            <div className="hold-small">Patient ID: {patientId || 'N/A'}</div>
          </div>

          <div>
            <label>Current / Old Authorization</label>
            <select
              value={selectedAuthId}
              onChange={(e) => setSelectedAuthId(e.target.value)}
              disabled={!patientId}
            >
              <option value="">
                {patientId
                  ? authorizations.length > 0
                    ? 'Select current authorization'
                    : 'No authorizations found'
                  : 'Select patient first'}
              </option>

              {authorizations.map((auth) => (
                <option
                  key={auth.authId || auth.id}
                  value={auth.authId || auth.id}
                >
                  {getAuthLabel(auth)}
                </option>
              ))}
            </select>

            {selectedAuth && (
              <div className="hold-small">
                Start: {formatDate(selectedAuth.dayStart)} | End:{' '}
                {formatDate(selectedAuth.dayEnd)}
              </div>
            )}
          </div>

          <div>
            <button type="button" onClick={openDoctorSearch}>
              Assign Doctor
            </button>

            <input
              type="text"
              value={assignedDoctorName}
              readOnly
              placeholder="Assigned Doctor"
            />

            <div className="hold-small">
              Doctor ID: {assignedDoctorId || 'N/A'}
            </div>
          </div>
        </div>

        <label>
          Pending New Authorization Code
          <input
            type="text"
            value={pendingAuthCode}
            onChange={(e) => setPendingAuthCode(e.target.value)}
            placeholder="Code used to check status while waiting"
          />
        </label>

        <div className="hold-create-flag-section">
          <strong>Hold Flags</strong>

          <p className="hold-small">
            Select any flags that apply to this hold. These come from Clinical Setup.
          </p>

          {activeFlagDefinitions.length === 0 ? (
            <div className="hold-empty-small">
              No active flags found. Add flags from Clinical Setup.
            </div>
          ) : (
            <div className="hold-create-flag-grid">
              {activeFlagDefinitions.map((flag) => {
                const checked = selectedCreateFlagIds.includes(
                  Number(flag.flagId)
                );

                return (
                  <label
                    key={flag.flagId}
                    className={`hold-create-flag-option hold-flag-color-${
                      flag.color || 'none'
                    } ${checked ? 'selected' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleCreateFlag(flag.flagId)}
                    />

                    <span>
                      <strong>{flag.label}</strong>
                      {flag.description && <small>{flag.description}</small>}
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        <label>
          Requested Superbill Dates
          <input
            type="text"
            value={requestedSuperbillDates}
            onChange={(e) => setRequestedSuperbillDates(e.target.value)}
            placeholder="Optional: 2026-05-01,2026-05-03"
          />
        </label>

        <textarea
          value={holdReason}
          onChange={(e) => setHoldReason(e.target.value)}
          placeholder="Reason / notes needed"
        />

        <div className="hold-action-grid">
          <button type="button" disabled={creating} onClick={createHold}>
            {creating ? 'Saving...' : 'Save Hold'}
          </button>

          <button
            type="button"
            className="hold-btn-secondary"
            onClick={resetCreateForm}
          >
            Clear
          </button>
        </div>
      </div>
    );
  };

  const renderPendingAuthCodePanel = (hold) => {
    const code =
      pendingAuthCodeByHold[hold.holdId] !== undefined
        ? pendingAuthCodeByHold[hold.holdId]
        : hold.pendingAuthCode || '';

    const notes =
      authorizationNotesByHold[hold.holdId] !== undefined
        ? authorizationNotesByHold[hold.holdId]
        : hold.authorizationNotes || '';

    return (
      <div className="hold-section">
        <strong>Pending Authorization Code</strong>

        <p className="hold-small">
          Store or update the authorization code while this request is still
          waiting. This does not approve or close the hold.
        </p>

        <input
          type="text"
          value={code}
          onChange={(e) =>
            setPendingAuthCodeByHold((prev) => ({
              ...prev,
              [hold.holdId]: e.target.value,
            }))
          }
          placeholder="Pending authorization code"
        />

        <textarea
          value={notes}
          onChange={(e) =>
            setAuthorizationNotesByHold((prev) => ({
              ...prev,
              [hold.holdId]: e.target.value,
            }))
          }
          placeholder="Authorization notes / follow-up notes"
        />

        <div className="hold-action-grid">
          <button type="button" onClick={() => savePendingAuthCode(hold)}>
            Save Pending Auth Code
          </button>
        </div>
      </div>
    );
  };

  const renderApprovedAuthCodeSection = (hold, form, key, label, type) => {
    const codes = form[key] || '';
    const isDiagnosis = key === 'diagnosisSet';

    return (
      <div className="hold-approved-code-box">
        <div className="hold-diagnosis-title-row">
          <div>
            <strong>{label}</strong>
            <p className="hold-small">
              Select codes for the new approved authorization.
            </p>
          </div>
        </div>

        {renderCodeChips({
          codes,
          emptyText: `No ${label.toLowerCase()} selected.`,
          chipClassName: isDiagnosis
            ? 'hold-diagnosis-chip'
            : 'hold-procedure-chip',
          removeClassName: 'hold-diagnosis-chip-remove',
          onRemove: (code) => removeApprovedAuthCode(hold.holdId, key, code),
        })}

        <div className="hold-action-grid">
          <button
            type="button"
            className="hold-btn-add-diagnosis"
            onClick={() =>
              type === 'diagnosis'
                ? openApprovedAuthDiagnosisSearch(hold.holdId)
                : openApprovedAuthProcedureSearch(hold.holdId)
            }
          >
            + Add {label}
          </button>

          <button
            type="button"
            className="hold-btn-secondary"
            onClick={() =>
              isDiagnosis
                ? copyDiagnosisFromHold(hold)
                : copyProcedureFromHold(hold)
            }
          >
            Copy From Hold
          </button>

          {parseCommaCodes(codes).length > 0 && (
            <button
              type="button"
              className="hold-btn-clear-diagnosis"
              onClick={() =>
                clearApprovedAuthCodes(hold.holdId, key, label.toLowerCase())
              }
            >
              Clear {label}
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderApprovedAuthForm = (hold) => {
    const form = getApprovedAuthForm(hold);

    return (
      <div className="hold-section hold-approved-auth-section">
        <strong>Create Approved Authorization</strong>

        <p className="hold-small">
          This creates a new authorization record and saves the selected
          diagnosis/procedure under that new authorization.
        </p>

        <div className="hold-create-grid">
          <label>
            Approved Auth Code
            <input
              type="text"
              value={form.authCode || ''}
              onChange={(e) =>
                updateApprovedAuthForm(hold.holdId, 'authCode', e.target.value)
              }
              placeholder="New approved auth code"
            />
          </label>

          <label>
            Start Date
            <input
              type="date"
              value={form.startDate || ''}
              onChange={(e) =>
                updateApprovedAuthForm(hold.holdId, 'startDate', e.target.value)
              }
            />
          </label>

          <label>
            End Date
            <input
              type="date"
              value={form.endDate || ''}
              onChange={(e) =>
                updateApprovedAuthForm(hold.holdId, 'endDate', e.target.value)
              }
            />
          </label>

          <label>
            Total Visits
            <input
              type="number"
              value={form.totalVisits || ''}
              onChange={(e) =>
                updateApprovedAuthForm(
                  hold.holdId,
                  'totalVisits',
                  e.target.value
                )
              }
              placeholder="Total approved visits"
            />
          </label>

          <label>
            Active
            <select
              value={form.active || 'yes'}
              onChange={(e) =>
                updateApprovedAuthForm(hold.holdId, 'active', e.target.value)
              }
            >
              <option value="yes">yes</option>
              <option value="no">no</option>
              <option value="active">active</option>
              <option value="inactive">inactive</option>
            </select>
          </label>

          <label>
            Referred By
            <input
              type="text"
              value={form.referredBy || ''}
              onChange={(e) =>
                updateApprovedAuthForm(
                  hold.holdId,
                  'referredBy',
                  e.target.value
                )
              }
              placeholder="Optional"
            />
          </label>
        </div>

        <div className="hold-section">
          <strong>Existing Clinical Episode (Optional)</strong>
          <p className="hold-small">
            Select an ACTIVE episode from this patient's previous evaluation to
            continue the same course of care across authorizations, or leave
            unassigned. Approval does not create a clinical episode or clinical
            profile. New episodes require a proper Initial Evaluation.
          </p>
          <label>
            Connect to clinical episode
            <select
              value={form.clinicalEpisodeId || ''}
              onChange={(e) => updateApprovedAuthForm(
                hold.holdId, 'clinicalEpisodeId', e.target.value
              )}
              disabled={!!clinicalEpisodeLoadingByPatient[hold.patientId] ||
                !Array.isArray(clinicalEpisodesByPatient[hold.patientId])}
            >
              <option value="">Do not link an episode (optional)</option>
              {(clinicalEpisodesByPatient[hold.patientId] || [])
                .filter((episode) => String(episode.status).toUpperCase() === 'ACTIVE')
                .map((episode) => {
                  const part = getBodyPartById(episode.bodyPartTemplateId);
                  const partName = part?.displayName || part?.bodyPartCode ||
                    `Body Part #${episode.bodyPartTemplateId}`;
                  return (
                    <option key={episode.clinicalEpisodeId} value={episode.clinicalEpisodeId}>
                      {`Episode #${episode.clinicalEpisodeId} | ${partName} | ` +
                        `${episode.laterality || 'N/A'} | Started ${formatDate(episode.episodeStartDate)}`}
                    </option>
                  );
                })}
            </select>
          </label>
          {clinicalEpisodeLoadingByPatient[hold.patientId] && (
            <p className="hold-small">Loading existing clinical episodes...</p>
          )}
          {clinicalEpisodeErrorByPatient[hold.patientId] && (
            <div className="hold-error" role="alert">
              {clinicalEpisodeErrorByPatient[hold.patientId]}
              <p className="hold-small">
                You can still approve without selecting an episode. Linking
                requires the patient-episode lookup endpoint to be available.
              </p>
            </div>
          )}
          {!clinicalEpisodeLoadingByPatient[hold.patientId] &&
            !clinicalEpisodeErrorByPatient[hold.patientId] &&
            Array.isArray(clinicalEpisodesByPatient[hold.patientId]) &&
            !clinicalEpisodesByPatient[hold.patientId].some(
              (episode) => String(episode.status).toUpperCase() === 'ACTIVE'
            ) && (
              <p className="hold-small">No ACTIVE clinical episodes found for this patient.</p>
            )}
          <button type="button" className="hold-btn-secondary"
            onClick={() => loadClinicalEpisodesForPatient(hold.patientId, true)}
            disabled={!!clinicalEpisodeLoadingByPatient[hold.patientId]}>
            Refresh Clinical Episodes
          </button>
        </div>

        {renderApprovedAuthCodeSection(
          hold,
          form,
          'diagnosisSet',
          'Diagnosis',
          'diagnosis'
        )}

        {renderApprovedAuthCodeSection(
          hold,
          form,
          'procedureSet',
          'Procedure',
          'procedure'
        )}

        <textarea
          value={form.resultNotes || ''}
          onChange={(e) =>
            updateApprovedAuthForm(hold.holdId, 'resultNotes', e.target.value)
          }
          placeholder="Approval/result notes"
        />

        <div className="hold-action-grid">
          <button
            type="button"
            className="hold-btn-save"
            onClick={() => createApprovedAuthorizationFromHold(hold)}
            disabled={!!approvedAuthSavingByHold[hold.holdId]}
          >
            {approvedAuthSavingByHold[hold.holdId] ? 'Approving...' : 'Create New Auth + Close Approved'}
          </button>
        </div>
      </div>
    );
  };

  const renderPanelToggleButton = (hold, sectionName, label, icon) => {
    const active = isHoldSectionExpanded(hold.holdId, sectionName);

    return (
      <button
        type="button"
        className={active ? 'active' : ''}
        onClick={() => toggleHoldSection(hold, sectionName)}
      >
        <span aria-hidden="true">{icon}</span>
        {active ? `Hide ${label}` : label}
      </button>
    );
  };

  const renderDetailsPanel = (hold) => {
    return (
      <div className="hold-collapsible-panel">
        <div className="hold-panel-title">Hold Details</div>

        <div className="hold-compact-info-grid">
          {renderInfoItem('Created', formatDateTime(hold.createdAt))}
          {renderInfoItem('Status', statusLabel(hold.status))}
          {renderInfoItem(
            'Before On Hold',
            hold.statusBeforeOnHold ? statusLabel(hold.statusBeforeOnHold) : 'N/A'
          )}
          {renderInfoItem('Assigned Doctor', hold.assignedDoctorName)}
          {renderInfoItem('Old Auth', hold.authCode)}
          {renderInfoItem('Pending Code', hold.pendingAuthCode)}
          {renderInfoItem('Approved Auth', hold.approvedAuthCode)}
          {renderInfoItem('Flags', getFlagSummaryText(hold))}
          {renderInfoItem(
            'Requested Dates',
            hold.requestedSuperbillDates || 'N/A'
          )}
        </div>

        <div className="hold-notes-box">
          <strong>Hold Reason</strong>
          <p>{hold.holdReason || 'N/A'}</p>
        </div>

        {(hold.onHoldNotes || hold.putOnHoldAt || hold.resumedAt) && (
          <div className="hold-notes-box hold-on-hold-note-box">
            <strong>On Hold / Resume Notes</strong>
            <p>{hold.onHoldNotes || 'N/A'}</p>

            <div className="hold-compact-info-grid hold-note-meta-grid">
              {renderInfoItem('Put On Hold By', hold.putOnHoldByStaffName)}
              {renderInfoItem('Put On Hold At', formatDateTime(hold.putOnHoldAt))}
              {renderInfoItem('Resumed By', hold.resumedByStaffName)}
              {renderInfoItem('Resumed At', formatDateTime(hold.resumedAt))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderPatientInfoPanel = (hold) => {
    return (
      <div className="hold-collapsible-panel">
        <div className="hold-panel-title">Patient / Insurance Info</div>

        <div className="hold-compact-info-grid">
          {renderInfoItem('Patient', hold.patientName || `Patient #${hold.patientId}`)}
          {renderInfoItem('Patient ID', hold.patientId)}
          {renderInfoItem('DOB', formatDate(hold.patientDateOfBirth))}
          {renderInfoItem('Insurance', hold.insuranceName)}
          {renderInfoItem('Subscriber ID', hold.subscriberId)}
          {renderInfoItem('Insurance Patient ID', hold.insptId)}
          {renderInfoItem(
            'Auth Dates',
            `${formatDate(hold.authStartDate)} to ${formatDate(
              hold.authEndDate
            )}`
          )}
          {renderInfoItem(
            'Visits',
            `Remaining: ${hold.remainingVisits ?? 'N/A'} / Total: ${
              hold.totalVisits ?? 'N/A'
            }`
          )}
        </div>
      </div>
    );
  };

  const renderDatesPanel = (hold) => {
    return (
      <div className="hold-collapsible-panel">
        <div className="hold-panel-title">Related Dates / Superbills</div>
        {renderSuperbillList(hold)}
      </div>
    );
  };

  const renderDiagnosisPanel = (hold) => {
    if (!hold.newEval || !canDoctorNotes) {
      return null;
    }

    return (
      <div className="hold-section hold-diagnosis-section">
        <div className="hold-diagnosis-title-row">
          <div>
            <strong>New Eval Diagnosis Update</strong>
            <p className="hold-small">
              Procedure stays unchanged. Add or remove diagnosis codes below,
              then save.
            </p>
          </div>
        </div>

        {renderCodeChips({
          codes: getDiagnosisTextForHold(hold),
          emptyText: 'No diagnoses selected.',
          chipClassName: 'hold-diagnosis-chip',
          removeClassName: 'hold-diagnosis-chip-remove',
          onRemove: (code) => removeDiagnosisFromHold(hold, code),
        })}

        <div className="hold-action-grid">
          <button
            type="button"
            className="hold-btn-add-diagnosis"
            onClick={() => openDiagnosisSearch(hold.holdId)}
          >
            + Add Diagnosis
          </button>

          <button
            type="button"
            className="hold-btn-save-diagnosis"
            onClick={() => updateDiagnosisForHold(hold)}
          >
            Save Diagnosis Changes
          </button>

          {parseCommaCodes(getDiagnosisTextForHold(hold)).length > 0 && (
            <button
              type="button"
              className="hold-btn-clear-diagnosis"
              onClick={() => clearDiagnosisForHold(hold)}
            >
              Clear All Diagnoses
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderWorkflowControls = (hold) => {
    if (!canManageAuth) {
      return null;
    }

    const status = String(hold.status || '').toUpperCase();
    const isResult = ['APPROVED', 'DENIED', 'CANCELLED', 'CLOSED', 'NO_AUTHORIZATION_NEEDED'].includes(status);

    if (isResult) {
      return null;
    }

    return (
      <div className="hold-section hold-workflow-control-section">
        <strong>Workflow Controls</strong>

        <p className="hold-small">
          Use these when the hold needs to pause, resume, or move back one step.
        </p>

        <textarea
          value={getWorkflowNote(hold.holdId)}
          onChange={(e) => setWorkflowNote(hold.holdId, e.target.value)}
          placeholder="Optional reason / follow-up note"
        />

        <div className="hold-action-grid hold-workflow-action-grid">
          {status === 'ON_HOLD' ? (
            <button
              type="button"
              className="hold-btn-save"
              onClick={() => resumeHold(hold)}
            >
              Resume Hold
            </button>
          ) : (
            <button
              type="button"
              className="hold-btn-warning"
              onClick={() => putHoldOnHold(hold)}
            >
              Put On Hold
            </button>
          )}

          {status !== 'WAITING_FOR_NOTES' && (
            <button
              type="button"
              className="hold-btn-secondary"
              onClick={() => backtrackHold(hold)}
            >
              Move Back One Step
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderActionsPanel = (hold, sectionKey) => {
    const showNewApprovedAuth = isHoldSectionExpanded(
      hold.holdId,
      'newAuthorization'
    );

    const notesSection =
      sectionKey === 'waitingForNotes' && canDoctorNotes ? (
        <div className="hold-section">
          <textarea
            value={
              doctorNotesByHold[hold.holdId] !== undefined
                ? doctorNotesByHold[hold.holdId]
                : hold.doctorNotes || ''
            }
            onChange={(e) =>
              setDoctorNotesByHold((prev) => ({
                ...prev,
                [hold.holdId]: e.target.value,
              }))
            }
            placeholder="Doctor notes for authorization"
          />

          <div className="hold-action-grid">
            <button type="button" onClick={() => startNotes(hold)}>
              Start Notes
            </button>

            <button
              type="button"
              className="hold-btn-save"
              onClick={() => markNotesComplete(hold)}
            >
              Complete Notes + All Superbills
            </button>
          </div>
        </div>
      ) : null;

    const notesCompleteSection =
      sectionKey === 'notesComplete' && canManageAuth ? (
        <>
          {renderPendingAuthCodePanel(hold)}

          <div className="hold-section">
            <strong>Authorization Submission</strong>

            <p className="hold-small">
              Pending new auth code:{' '}
              <strong>{hold.pendingAuthCode || 'N/A'}</strong>
            </p>

            <div className="hold-action-grid">
              <button type="button" onClick={() => sendToAuthorization(hold)}>
                Send To Authorization
              </button>
            </div>
          </div>

          <div className="hold-section">
            <strong>No Authorization Needed</strong>

            <textarea
              value={noAuthNotesByHold[hold.holdId] || ''}
              onChange={(e) =>
                setNoAuthNotesByHold((prev) => ({
                  ...prev,
                  [hold.holdId]: e.target.value,
                }))
              }
              placeholder="Reason/result notes"
            />

            <div className="hold-action-grid">
              <button
                type="button"
                className="hold-btn-secondary"
                onClick={() => skipNoAuthorizationNeeded(hold)}
              >
                Close as No Authorization Needed
              </button>
            </div>
          </div>
        </>
      ) : null;

    const authorizationSection =
      sectionKey === 'authorization' && canManageAuth ? (
        <>
          {renderPendingAuthCodePanel(hold)}

          {hold.status === 'SENT_TO_AUTHORIZATION' && (
            <div className="hold-section">
              <strong>Authorization Status</strong>

              <p>
                Sent to authorization at:{' '}
                {formatDateTime(hold.sentToAuthorizationAt)}
              </p>

              <div className="hold-action-grid">
                <button type="button" onClick={() => markWaiting(hold)}>
                  Mark Waiting for Authorization
                </button>
              </div>
            </div>
          )}

          {hold.status === 'WAITING_FOR_AUTHORIZATION' && (
            <div className="hold-section">
              <strong>Authorization Result</strong>

              <select
                value={resultStatusByHold[hold.holdId] || ''}
                onChange={(e) =>
                  setResultStatusByHold((prev) => ({
                    ...prev,
                    [hold.holdId]: e.target.value,
                  }))
                }
              >
                <option value="">Select result</option>

                {RESULT_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {statusLabel(status)}
                  </option>
                ))}
              </select>

              <textarea
                value={resultNotesByHold[hold.holdId] || ''}
                onChange={(e) =>
                  setResultNotesByHold((prev) => ({
                    ...prev,
                    [hold.holdId]: e.target.value,
                  }))
                }
                placeholder="Result notes"
              />

              <div className="hold-action-grid">
                <button
                  type="button"
                  className="hold-btn-save"
                  onClick={() => enterResult(hold)}
                >
                  Save Result Only
                </button>
              </div>
            </div>
          )}

          <div className="hold-section">
            <button
              type="button"
              className="hold-new-auth-toggle"
              onClick={() => toggleHoldSection(hold, 'newAuthorization')}
            >
              {showNewApprovedAuth
                ? 'Hide New Approved Authorization'
                : '+ New Approved Authorization'}
            </button>

            {showNewApprovedAuth && renderApprovedAuthForm(hold)}
          </div>
        </>
      ) : null;

    const onHoldSection =
      sectionKey === 'onHold' ? (
        <div className="hold-section hold-on-hold-active-section">
          <strong>This hold is currently on hold</strong>

          <div className="hold-compact-info-grid">
            {renderInfoItem(
              'Previous Step',
              hold.statusBeforeOnHold ? statusLabel(hold.statusBeforeOnHold) : 'N/A'
            )}
            {renderInfoItem('Put On Hold By', hold.putOnHoldByStaffName)}
            {renderInfoItem('Put On Hold At', formatDateTime(hold.putOnHoldAt))}
          </div>

          <div className="hold-notes-box">
            <strong>On Hold Notes</strong>
            <p>{hold.onHoldNotes || 'N/A'}</p>
          </div>
        </div>
      ) : null;

    const resultSection =
      sectionKey === 'results' ? (
        <div className="hold-section">
          <div className="hold-compact-info-grid">
            {renderInfoItem('Status', statusLabel(hold.resultStatus || hold.status))}
            {renderInfoItem('Pending Auth Code', hold.pendingAuthCode)}
            {renderInfoItem(
              'Approved Auth',
              hold.approvedAuthCode ||
                (hold.approvedAuthId ? `#${hold.approvedAuthId}` : 'N/A')
            )}
            {renderInfoItem('Closed', formatDateTime(hold.closedAt))}
          </div>

          <div className="hold-notes-box">
            <strong>Result Notes</strong>
            <p>{hold.resultNotes || 'N/A'}</p>
          </div>
        </div>
      ) : null;

    const deleteSection =
      canDelete ? (
        <div className="hold-action-grid hold-danger-zone">
          <button type="button" onClick={() => deleteHold(hold)}>
            Delete Hold
          </button>
        </div>
      ) : null;

    return (
      <div className="hold-collapsible-panel">
        <div className="hold-panel-title">Actions</div>

        <p className="hold-small hold-actions-help">
          Open only the section you need. This keeps large holds easier to work
          with.
        </p>

        {renderActionAccordionSection(
          hold,
          'flags',
          'Hold Flags',
          'Add or remove labels used for tracking and rules.',
          renderFlagsPanel(hold),
          true
        )}

        {renderActionAccordionSection(
          hold,
          'workflow',
          'Workflow Controls',
          'Pause, resume, or move the hold back one workflow step.',
          renderWorkflowControls(hold)
        )}

        {renderActionAccordionSection(
          hold,
          'diagnosis',
          'Diagnosis Update',
          'Only available for Eval holds.',
          renderDiagnosisPanel(hold)
        )}

        {renderActionAccordionSection(
          hold,
          'notes',
          'Notes',
          'Start or complete the notes step.',
          notesSection,
          sectionKey === 'waitingForNotes'
        )}

        {renderActionAccordionSection(
          hold,
          'notesComplete',
          'Notes Complete Actions',
          'Save pending auth codes, submit to authorization, or close as no auth needed.',
          notesCompleteSection,
          sectionKey === 'notesComplete'
        )}

        {renderActionAccordionSection(
          hold,
          'authorization',
          'Authorization Actions',
          'Update pending codes, mark waiting, save a result, or create a new authorization.',
          authorizationSection,
          sectionKey === 'authorization'
        )}

        {renderActionAccordionSection(
          hold,
          'onHoldStatus',
          'On Hold Status',
          'View current on-hold details.',
          onHoldSection,
          sectionKey === 'onHold'
        )}

        {renderActionAccordionSection(
          hold,
          'result',
          'Result',
          'View final result information.',
          resultSection,
          sectionKey === 'results'
        )}

        {renderActionAccordionSection(
          hold,
          'delete',
          'Delete Hold',
          'Manager/Admin only. Removes this authorization hold.',
          deleteSection
        )}
      </div>
    );
  };

  const renderHoldCard = (hold, sectionKey) => {
    const detailsOpen = isHoldSectionExpanded(hold.holdId, 'details');
    const datesOpen = isHoldSectionExpanded(hold.holdId, 'dates');
    const patientInfoOpen = isHoldSectionExpanded(hold.holdId, 'patientInfo');
    const filesOpen = isHoldSectionExpanded(hold.holdId, 'files');
    const actionsOpen = isHoldSectionExpanded(hold.holdId, 'actions');

    return (
      <div className="hold-card" key={hold.holdId}>
        <div className="hold-card-top-row">
          <div className="hold-card-title-area">
            <h2>
              {hold.patientName || `Patient #${hold.patientId}`} — Hold #
              {hold.holdId}
            </h2>

            <div className="hold-card-meta-line">
              <span>
                <strong>Old Auth:</strong> {hold.authCode || 'N/A'}
              </span>

              <span>
                <strong>Pending:</strong> {hold.pendingAuthCode || 'N/A'}
              </span>

              <span>
                <strong>Doctor:</strong> {hold.assignedDoctorName || 'N/A'}
              </span>
            </div>
          </div>

          <div className="hold-card-header-tools">
            <span className={getStatusClass(hold.status)}>
              {statusLabel(hold.status)}
            </span>

            <button
              type="button"
              className="hold-expand-window-button"
              onClick={() => openHoldPopup(hold, 'overview', sectionKey)}
            >
              View Larger
            </button>

            <button
              type="button"
              className="hold-print-icon-button"
              title="Print / Export Hold"
              aria-label="Print or export this hold"
              onClick={() => exportSingleHold(hold)}
            >
              🖨
            </button>
          </div>
        </div>

        <div className="hold-card-summary-mini">
          <div>
            <span>DOB</span>
            <strong>{formatDate(hold.patientDateOfBirth)}</strong>
          </div>

          <div>
            <span>Insurance</span>
            <strong>{hold.insuranceName || 'N/A'}</strong>
          </div>

          <div>
            <span>Flags</span>
            <strong>{getFlagCountForHold(hold)}</strong>
          </div>

          <div>
            <span>Superbills</span>
            <strong>
              {Array.isArray(hold.superbills) ? hold.superbills.length : 0}
            </strong>
          </div>
        </div>

        {renderFlagPreview(hold)}

        <div className="hold-card-note-preview">
          <strong>Notes:</strong>
          <span>{getHoldNotePreview(hold)}</span>
        </div>

        <div className="hold-card-quick-actions">
          {renderPanelToggleButton(hold, 'details', 'Details', detailsOpen ? '▾' : '▸')}
          {renderPanelToggleButton(hold, 'dates', 'Dates', datesOpen ? '▾' : '📅')}
          {renderPanelToggleButton(
            hold,
            'patientInfo',
            'Patient Info',
            patientInfoOpen ? '▾' : '👤'
          )}
          {renderPanelToggleButton(hold, 'files', 'Files', filesOpen ? '▾' : '📁')}
          {renderPanelToggleButton(hold, 'actions', 'Actions', actionsOpen ? '▾' : '⚙')}
        </div>

        {detailsOpen && renderDetailsPanel(hold)}
        {datesOpen && renderDatesPanel(hold)}
        {patientInfoOpen && renderPatientInfoPanel(hold)}
        {filesOpen && renderPatientFilesPanel(hold)}
        {actionsOpen && renderActionsPanel(hold, sectionKey)}
      </div>
    );
  };

  const renderResultsPagination = () => {
    if (resultsLoading) return <p className="hold-loading-text">Loading historical results...</p>;
    if (resultsError) return <div className="hold-error">{resultsError}</div>;
    if (!resultsData.content.length) return <div className="hold-empty">No historical results found.</div>;
    return (
      <>
        <div className="hold-card-grid">
          {resultsData.content.map((hold) => renderHoldCard(hold, 'results'))}
        </div>
        <div className="hold-results-pager" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', flexWrap: 'wrap', margin: '16px 0' }}>
          <button type="button" disabled={resultsPage === 0} onClick={() => setResultsPage((n) => Math.max(0, n - 1))}>Previous</button>
          <span>Page {resultsData.number + 1} of {resultsData.totalPages} · {resultsData.totalElements} results</span>
          <button type="button" disabled={resultsPage + 1 >= resultsData.totalPages} onClick={() => setResultsPage((n) => n + 1)}>Next</button>
        </div>
      </>
    );
  };

  const renderActiveTabContent = () => {
    if (normalizedPatientSearchTerm) {
      const sortedMatches = [...searchMatchedHolds].sort(
        (a, b) => Number(a.hold.holdId || 0) - Number(b.hold.holdId || 0)
      );
      return (
        <>
          <h3>Active Holds ({sortedMatches.length})</h3>
          {sortedMatches.length ? (
            <div className="hold-card-grid">
              {sortedMatches.map(({ hold, sectionKey }) => renderHoldCard(hold, sectionKey))}
            </div>
          ) : <div className="hold-empty">No matching active holds.</div>}
          <h3 style={{ marginTop: '20px' }}>Historical Results {resultsLoading ? '' : `(${resultsData.totalElements})`}</h3>
          {debouncedSearchTerm !== patientSearchTerm.trim()
            ? <p className="hold-loading-text">Searching historical results...</p>
            : renderResultsPagination()}
        </>
      );
    }

    if (activeTab === 'create') {
      return renderCreateTab();
    }
    if (activeTab === 'results') {
      return renderResultsPagination();
    }

    let holds = [];

    if (activeTab === 'authorization') {
      holds = authorizationHolds;
    } else {
      holds = board[activeTab] || [];
    }

    if (holds.length === 0) {
      return <div className="hold-empty">No holds in this step.</div>;
    }

    const sortedHolds = [...holds].sort((a, b) => {
      const aId = Number(a.holdId || 0);
      const bId = Number(b.holdId || 0);
      return aId - bId;
    });

    return (
      <div className="hold-card-grid">
        {sortedHolds.map((hold) => renderHoldCard(hold, activeTab))}
      </div>
    );
  };

  const renderFocusedPanelContent = () => {
    if (!focusedHold) {
      return null;
    }

    const sectionKey = focusedSectionKey || getSectionKeyForHold(focusedHold);

    if (focusedPanel === 'details') {
      return renderDetailsPanel(focusedHold);
    }

    if (focusedPanel === 'dates') {
      return renderDatesPanel(focusedHold);
    }

    if (focusedPanel === 'patientInfo') {
      return renderPatientInfoPanel(focusedHold);
    }

    if (focusedPanel === 'files') {
      return renderPatientFilesPanel(focusedHold);
    }

    if (focusedPanel === 'actions') {
      return renderActionsPanel(focusedHold, sectionKey);
    }

    return (
      <>
        {renderDetailsPanel(focusedHold)}
        {renderPatientInfoPanel(focusedHold)}
      </>
    );
  };

  const renderClinicalProfileModal = () => {
    if (!clinicalProfileModal.open) {
      return null;
    }

    const lateralityOptions = getLateralityOptions(
      clinicalProfileModal.bodyPartTemplateId
    );

    return (
      <div className="hold-modal-backdrop" role="dialog" aria-modal="true">
        <div className="hold-modal-window" style={{ maxWidth: '760px' }}>
          <div className="hold-modal-header">
            <div>
              <h2>Authorization Clinical Profile</h2>
              <p>
                Authorization #{clinicalProfileModal.authId}
                {clinicalProfileModal.patientName
                  ? ` | ${clinicalProfileModal.patientName}`
                  : ''}
              </p>
            </div>

            <button
              type="button"
              className="hold-modal-close-button"
              onClick={closeClinicalProfileModal}
              disabled={clinicalProfileModal.saving}
            >
              Close
            </button>
          </div>

          <div className="hold-modal-content" style={{ padding: '20px' }}>
            <p className="hold-small">
              This authorization must be configured before its clinical note can
              be created. These values become the authorization-level source for
              the note body part, laterality, and signing provider.
            </p>

            {clinicalProfileModal.error && (
              <div className="hold-error">{clinicalProfileModal.error}</div>
            )}

            <div className="hold-create-grid">
              <label>
                Body Part
                <select
                  value={clinicalProfileModal.bodyPartTemplateId || ''}
                  onChange={(e) =>
                    handleClinicalProfileBodyPartChange(e.target.value)
                  }
                  disabled={clinicalProfileModal.saving}
                >
                  <option value="">Select body part</option>
                  {activeBodyParts.map((bodyPart) => (
                    <option
                      key={bodyPart.bodyPartTemplateId}
                      value={bodyPart.bodyPartTemplateId}
                    >
                      {bodyPart.displayName ||
                        bodyPart.bodyPartCode ||
                        `Body Part #${bodyPart.bodyPartTemplateId}`}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                Laterality
                <select
                  value={clinicalProfileModal.laterality || ''}
                  onChange={(e) =>
                    updateClinicalProfileModal('laterality', e.target.value)
                  }
                  disabled={
                    clinicalProfileModal.saving ||
                    !clinicalProfileModal.bodyPartTemplateId
                  }
                >
                  <option value="">Select laterality</option>
                  {lateralityOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>

              <div>
                <label>Treating / Signing Provider</label>

                <button
                  type="button"
                  onClick={() =>
                    openClinicalProviderSearch(
                      'authorizationClinicalProfileProvider'
                    )
                  }
                  disabled={clinicalProfileModal.saving}
                >
                  Select Provider
                </button>

                <input
                  type="text"
                  value={clinicalProfileModal.providerName || ''}
                  readOnly
                  placeholder={
                    clinicalProfileModal.providerId
                      ? `Doctor #${clinicalProfileModal.providerId}`
                      : 'Selected provider'
                  }
                />

                <div className="hold-small">
                  Provider ID: {clinicalProfileModal.providerId || 'N/A'}
                </div>
              </div>
            </div>

            <div className="hold-action-grid" style={{ marginTop: '18px' }}>
              <button
                type="button"
                className="hold-btn-save"
                onClick={saveClinicalProfileAndOpenNote}
                disabled={clinicalProfileModal.saving}
              >
                {clinicalProfileModal.saving
                  ? 'Saving + Opening...'
                  : 'Save & Open Clinical Note'}
              </button>

              <button
                type="button"
                className="hold-btn-secondary"
                onClick={closeClinicalProfileModal}
                disabled={clinicalProfileModal.saving}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderHoldPopup = () => {
    if (!focusedHold) {
      return null;
    }

    return (
      <div className="hold-modal-backdrop" role="dialog" aria-modal="true">
        <div className="hold-modal-window">
          <div className="hold-modal-header">
            <div>
              <h2>
                {focusedHold.patientName || `Patient #${focusedHold.patientId}`} — Hold #
                {focusedHold.holdId}
              </h2>

              <p>
                {focusedHold.insuranceName || 'No Insurance'} | DOB:{' '}
                {formatDate(focusedHold.patientDateOfBirth)} | Status:{' '}
                {statusLabel(focusedHold.status)}
              </p>
            </div>

            <div className="hold-modal-header-actions">
              <button
                type="button"
                className="hold-print-icon-button"
                onClick={() => exportSingleHold(focusedHold)}
              >
                🖨
              </button>

              <button
                type="button"
                className="hold-modal-close-button"
                onClick={closeHoldPopup}
              >
                Close
              </button>
            </div>
          </div>

          <div className="hold-modal-body">
            <aside className="hold-modal-side-actions">
              <button
                type="button"
                className={focusedPanel === 'overview' ? 'active' : ''}
                onClick={() => setFocusedPanel('overview')}
              >
                Overview
              </button>

              <button
                type="button"
                className={focusedPanel === 'details' ? 'active' : ''}
                onClick={() => setFocusedPanel('details')}
              >
                Hold Details
              </button>

              <button
                type="button"
                className={focusedPanel === 'dates' ? 'active' : ''}
                onClick={() => setFocusedPanel('dates')}
              >
                Dates / Superbills
              </button>

              <button
                type="button"
                className={focusedPanel === 'patientInfo' ? 'active' : ''}
                onClick={() => setFocusedPanel('patientInfo')}
              >
                Patient Info
              </button>

              <button
                type="button"
                className={focusedPanel === 'files' ? 'active' : ''}
                onClick={() => {
                  setFocusedPanel('files');
                  loadPatientFilesForHold(focusedHold);
                }}
              >
                Files
              </button>

              <button
                type="button"
                className={focusedPanel === 'actions' ? 'active' : ''}
                onClick={() => setFocusedPanel('actions')}
              >
                Actions
              </button>
            </aside>

            <main className="hold-modal-content">
              {renderFocusedPanelContent()}
            </main>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="authorization-hold-page">
      <h1>Authorization Holds</h1>

      {error && <div className="hold-error">{error}</div>}
      {success && <div className="hold-success">{success}</div>}

      <div className="hold-toolbar">
        <label>
          Doctor Filter
          <select
            value={doctorFilter}
            onChange={(e) => setDoctorFilter(e.target.value)}
          >
            <option value="">All Assigned Doctors</option>

            {board.doctors.map((doctor) => (
              <option key={doctor.doctorId} value={doctor.doctorId}>
                {doctor.doctorName || `Doctor #${doctor.doctorId}`}
              </option>
            ))}
          </select>
        </label>

        <label className="hold-patient-search-label">
          Search Holds
          <div className="hold-patient-search-row">
            <input
              type="text"
              value={patientSearchTerm}
              onChange={(e) => setPatientSearchTerm(e.target.value)}
              placeholder="Search active and historical holds by patient, hold #, auth code"
            />

            {patientSearchTerm && (
              <button
                type="button"
                className="hold-clear-search-button"
                onClick={() => { setPatientSearchTerm(''); setResultsPage(0); }}
              >
                Clear
              </button>
            )}
          </div>
        </label>

        <button type="button" className="hold-refresh-small-button" onClick={() => {
          loadBoard();
        }}>
          Refresh
        </button>

        {role === 'ADMIN' && (
          <button
            type="button"
            className="hold-refresh-small-button"
            onClick={() => {
              window.location.href = '/clinical-template-setup';
            }}
          >
            Clinical Templates
          </button>
        )}

        <span className="hold-count">
          Active Holds: <strong>{allHoldsCount}</strong>
        </span>
      </div>

      <div className="hold-workflow-tabs">
        {WORKFLOW_TABS.map((tab) => {
          let count = 0;

          if (tab.key === 'authorization') {
            count = authorizationHolds.length;
          } else if (tab.key === 'results') {
            count = resultsData.totalElements;
          } else if (tab.key !== 'create') {
            count = board[tab.key]?.length || 0;
          }

          return (
            <button
              key={tab.key}
              type="button"
              className={activeTab === tab.key ? 'active' : ''}
              onClick={() => { setActiveTab(tab.key); setResultsPage(0); }}
            >
              {tab.title}
              {tab.key !== 'create' && <span>{count}</span>}
            </button>
          );
        })}
      </div>

      <div className="hold-active-step-header">
        <h2>
          {normalizedPatientSearchTerm ? (
            <>
              Search Results
              <span> ({searchMatchedHolds.length} active{!resultsLoading ? `, ${resultsData.totalElements} historical` : ''})</span>
            </>
          ) : (
            <>
              {WORKFLOW_TABS.find((tab) => tab.key === activeTab)?.title}
              {activeTab !== 'create' && <span> ({activeTabCount})</span>}
            </>
          )}
        </h2>
      </div>

      {loading ? (
        <p className="hold-loading-text">Loading authorization hold board...</p>
      ) : (
        renderActiveTabContent()
      )}

      {renderHoldPopup()}
      {renderClinicalProfileModal()}
    </div>
  );
};

export default AuthorizationHoldPage;